import { Link } from 'react-router'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowRight,
  Calendar,
  ChevronLeft,
  ChevronRight,
  MapPinned,
  LifeBuoy,
  AlertTriangle,
  Package,
  Plus,
  Settings,
  Truck,
  Users,
  Wallet,
  X,
  Zap,
  FileSpreadsheet,
  Clock3,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { ROLE_INFO } from '../config/permissions'
import { dashboardService } from '../services/dashboardService'
import { basePriceService } from '../services/basePriceService'
import { customerService } from '../services/customerService'
import { vehicleService } from '../services/vehicleService'
import { getGoogleMapsApiKey, getGoogleMapsApiKeySync } from '../utils/googleMaps'
import { warehouseService } from '../services/warehouseService'
import { supplyService } from '../services/supplyService'
import { supportService } from '../services/supportService'
import { driverIncidentReportService } from '../services/driverIncidentReportService'
import { seyirViolationService } from '../services/seyirViolationService'
import { SupplyFormModal } from '../components/SupplyFormModal'
import { ShipmentWizardSection, ShipmentWizardStepTabs } from '../components/ShipmentWizardShared'
import { MultiVehicleTypeSelect } from '../components/MultiVehicleTypeSelect'
import { ShipmentWizardModal } from '../components/ShipmentWizardModal'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { exportRowsToExcel } from '../utils/excelExport'
import { toast } from 'sonner'

type DashboardPoint = {
  id: string
  type: 'pickup' | 'delivery' | 'driver' | 'vehicle'
  shipmentId: number
  shipmentNo: string
  label: string
  subtitle: string
  latitude: number
  longitude: number
  status: string
  stage: number
  speed?: number
  isRunning?: boolean
  address?: string
  dailyKm?: number | null
  dailyFuel?: number | null
  fuelLevel?: number | null
  ignitionOn?: string | null
  ignitionOff?: string | null
  dailyIdleTime?: string | null
  dailyWorkTime?: string | null
  dailyDriveTime?: string | null
  activeShipmentId?: number | null
  activeShipmentNo?: string | null
  activeShipmentStatus?: string | null
  activeVehicleStatus?: string | null
  activeShipmentStage?: number | null
  activePickupName?: string | null
  activeDeliveryName?: string | null
  activeTransportModel?: string | null
}

type DashboardOverview = {
  company: {
    name: string
    subtitle: string
  }
  metrics: {
    availableVehicles: number
    totalFleetVehicles: number
    activeShipments: number
    unassignedOrders: number
    loadingVehicles: number
    transportVehicles: number
    deliveryApprovalPending: number
    invoiceWaiting: number
    paymentPending: number
    overdueSpotInvoices: number
    overdueOwnFleetDeliveries: number
    zeroKmOwnFleetDrivers: number
    approvedDrivers: number
    pendingApplications: number
    openBidRequests: number
    unusedDigitalLicenseDrivers72h: number
    legend: {
      activeTask: number
      available: number
      preparation: number
      loading: number
      transport: number
      delivery: number
    }
  }
  finance: {
    currentMonth: {
      shipmentCount: number
      ownFleetCount: number
      spotCount: number
      waitingTotal: number
      handlingTotal: number
      stopTotal: number
      totalKm: number
      totalSpotPayment: number
    }
    previousMonth: {
      shipmentCount: number
      ownFleetCount: number
      spotCount: number
      waitingTotal: number
      handlingTotal: number
      stopTotal: number
      totalKm: number
      totalSpotPayment: number
    }
  }
  overdueSpotInvoices: Array<{
    id: string
    shipmentNo: string
    planCode: string
    pickupName: string
    deliveryName: string
    pickupCity: string
    deliveryCity: string
    pickupDate: string | null
    accountingStatus: string
    overdueDays: number
    driverName: string
    driverPhone: string
    tractorPlate: string
    trailerPlate: string
  }>
  overdueOwnFleetDeliveries: Array<{
    id: string
    shipmentNo: string
    planCode: string
    pickupName: string
    deliveryName: string
    pickupCity: string
    deliveryCity: string
    pickupDate: string | null
    status: string
    currentStage: number
    overdueDays: number
    driverName: string
    driverPhone: string
    tractorPlate: string
    trailerPlate: string
  }>
  topOwnFleetVehicleCities: Array<{
    city: string
    count: number
  }>
  map: {
    points: DashboardPoint[]
  }
}

type VehicleAlertItem = {
  id: string
  plate: string
  trailerPlate: string
  title: string
  date: string
  daysLeft: number
}

type VehicleAlertModalType = 'insurance' | 'inspection' | 'maintenance' | null

type SeyirViolationRow = {
  id: number
  violation_type: string
  severity: string
  first_detected_at: string
  last_detected_at: string
  check_count: number
  tractor_plate: string | null
  trailer_plate: string | null
  system_driver_name: string | null
  system_driver_license_no: string | null
  seyir_driver_name: string | null
  seyir_digital_license_no: string | null
  matched_driver_name?: string | null
}

type UnusedDigitalLicenseDriver = {
  driverId: string
  driverName: string
  licenseNumber: string
  assignedPlate: string
  trailerPlate: string
  status: string
  lastCardSeenAt: string | null
  lastCardVehiclePlate: string
  unusedHours: number | null
  dailyUsage: Array<{
    usage_date: string
    vehicle_plate: string | null
    first_seen_at: string | null
    last_seen_at: string | null
    daily_km: number | null
    daily_drive_time: string | null
    daily_work_time: string | null
    snapshot_count: number
  }>
}

type NewSpotDriver = {
  id: number
  full_name: string
  phone: string | null
  city: string | null
  district: string | null
  status: string
  source: string | null
  registration_source: string
  reviewer_name: string | null
  created_at: string
}

type NewSpotDriversData = {
  counts: { today: number; week: number; month: number }
  drivers: { today: NewSpotDriver[]; week: NewSpotDriver[]; month: NewSpotDriver[] }
}

type WizardStep = 1 | 2 | 3

type RouteOption = {
  id: string
  type: 'customer' | 'warehouse'
  label: string
  city?: string
  district?: string
  address?: string
  latitude?: number | null
  longitude?: number | null
}

type IntermediateStop = {
  id: string
  label: string
  city?: string
  district?: string
  address?: string
  latitude?: number | null
  longitude?: number | null
}

type WizardForm = {
  tripType: 'komple' | 'parsiyel'
  pickupOptionId: string
  deliveryOptionId: string
  pickupLabel: string
  pickupCity: string
  pickupDistrict: string
  pickupAddress: string
  deliveryLabel: string
  deliveryCity: string
  deliveryDistrict: string
  deliveryAddress: string
  pickupLatitude: number | null
  pickupLongitude: number | null
  deliveryLatitude: number | null
  deliveryLongitude: number | null
  vehicleType: string[]
  loadType: string
  weightKg: number
  volumeM3: number
  palletCount: number
  vehicleCount: number
  distanceKm: number
  notes: string
  marginPercent: number
}

type CustomerCreateForm = {
  companyCode: string
  companyName: string
  taxNumber: string
  taxOffice: string
  contactPerson: string
  phone: string
  email: string
  paymentType: '7' | '14' | '30' | '45' | '60'
  address: string
  city: string
  district: string
  latitude: number | null
  longitude: number | null
}

const LOAD_TYPE_OPTIONS = ['Cips', 'Çay', 'İçecek', 'Gıda', 'Paletli Ürün', 'Dökme Yük', 'Ambalaj', 'Temizlik Ürünü', 'Tekstil', 'Koli']

const isVehicleStatusActive = (value?: string | null) => {
  const normalized = String(value || '').trim().toLocaleUpperCase('tr-TR')
  return ['YÜK VERİLDİ', 'YOLDA', 'YÜKLEMEDE', 'YÜKLEME BEKLİYOR', 'TAHLİYE BEKLİYOR'].includes(normalized)
}

const normalizeDateOnly = (value?: string | null) => {
  if (!value) return null
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toISOString().slice(0, 10)
}

const formatDateTimeTr = (value?: string | null) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('tr-TR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatDateOnlyTr = (value?: string | null) => {
  if (!value) return '-'
  const normalized = normalizeDateOnly(value)
  if (!normalized) return value
  const date = new Date(`${normalized}T00:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('tr-TR')
}

const DashboardPulseValue = ({ className = 'h-8 w-24' }: { className?: string }) => (
  <div className={`animate-pulse rounded-lg bg-slate-200 ${className}`} />
)

type SourceBreakdown = {
  label: string
  count: number
  color: string
}

const SOURCE_BREAKDOWN_COLORS: Record<string, string> = {
  'Sürücü Kaydı': '#10b981',
  'Panelden Eklendi': '#6366f1',
}

const normalizeDriverSource = (raw: string | null): 'Sürücü Kaydı' | 'Panelden Eklendi' => {
  const lower = String(raw || '').toLocaleLowerCase('tr-TR')
  if (lower.includes('panel')) return 'Panelden Eklendi'
  return 'Sürücü Kaydı'
}

const getSourceBreakdown = (drivers: NewSpotDriver[]): SourceBreakdown[] => {
  const counts: Record<string, number> = { 'Sürücü Kaydı': 0, 'Panelden Eklendi': 0 }
  for (const driver of drivers) {
    const category = normalizeDriverSource(driver.registration_source)
    counts[category]++
  }
  return [
    { label: 'Sürücü Kaydı', count: counts['Sürücü Kaydı'], color: SOURCE_BREAKDOWN_COLORS['Sürücü Kaydı'] },
    { label: 'Panelden Eklendi', count: counts['Panelden Eklendi'], color: SOURCE_BREAKDOWN_COLORS['Panelden Eklendi'] },
  ].filter((item) => item.count > 0)
}

const SourcePieChart = ({ breakdown, total }: { breakdown: SourceBreakdown[]; total: number }) => {
  if (total === 0) return <div className="flex h-32 items-center justify-center text-sm text-slate-400">Veri yok</div>
  let accumulated = 0
  const segments = breakdown.map((item) => {
    const start = accumulated
    const pct = total > 0 ? (item.count / total) * 100 : 0
    accumulated += pct
    return `${item.color} ${start}% ${accumulated}%`
  })
  return (
    <div className="flex items-center gap-6">
      <div
        className="h-28 w-28 rounded-full shadow-inner"
        style={{ background: `conic-gradient(${segments.join(', ')})` }}
      />
      <div className="space-y-2.5">
        {breakdown.map((item) => {
          const pct = total > 0 ? ((item.count / total) * 100).toFixed(1) : '0'
          return (
            <div key={item.label} className="flex items-center gap-3 text-sm">
              <span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: item.color }} />
              <span className="text-slate-600">{item.label}</span>
              <span className="font-semibold text-slate-900">{item.count}</span>
              <span className="text-xs text-slate-400">(%{pct})</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const exportDelayedShipments = (rows: any[], filePrefix: string) => {
  exportRowsToExcel(rows, `${filePrefix}-${new Date().toISOString().slice(0, 10)}.xlsx`, {
    sheetName: 'Sevkiyatlar',
    columns: [
      { key: 'shipmentNo', label: 'Sevk No' },
      { key: 'planCode', label: 'Plan' },
      { key: 'pickup', label: 'Yukleme', value: (row) => `${row.pickupName || '-'} / ${row.pickupCity || '-'}` },
      { key: 'delivery', label: 'Teslim', value: (row) => `${row.deliveryName || '-'} / ${row.deliveryCity || '-'}` },
      { key: 'pickupDate', label: 'Talep Tarihi', value: (row) => formatDateOnlyTr(row.pickupDate) },
      { key: 'overdueDays', label: 'Gecikme (Gun)' },
      { key: 'driver', label: 'Surucu', value: (row) => `${row.driverName || '-'}${row.driverPhone && row.driverPhone !== '-' ? ` / ${row.driverPhone}` : ''}` },
      { key: 'plates', label: 'Cekici / Dorse', value: (row) => `${row.tractorPlate || '-'} / ${row.trailerPlate || '-'}` },
    ],
  })
}

const getSeyirViolationLabel = (type?: string | null) => (
  type === 'unknown_digital_license' ? 'Bilinmeyen dijital ehliyet' : 'Atanan sürücü farklı'
)

const getDaysUntil = (value?: string | null) => {
  const normalized = normalizeDateOnly(value)
  if (!normalized) return null
  const target = new Date(`${normalized}T00:00:00`)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.ceil((target.getTime() - today.getTime()) / 86400000)
}

const buildVehicleAlertItems = (vehicles: any[], field: 'insurance_expiry' | 'license_expiry' | 'next_maintenance_date', title: string) => {
  return vehicles
    .map((vehicle) => {
      const date = vehicle?.[field]
      const daysLeft = getDaysUntil(date)
      if (daysLeft == null || daysLeft < 0 || daysLeft > 30) return null
      return {
        id: String(vehicle.id),
        plate: vehicle.cekici_plaka || vehicle.plate || vehicle.plate_number || '-',
        trailerPlate: vehicle.linked_trailer_plate || vehicle.dorse_plaka || '-',
        title,
        date: normalizeDateOnly(date) || String(date || ''),
        daysLeft,
      } satisfies VehicleAlertItem
    })
    .filter(Boolean)
    .sort((a, b) => a.daysLeft - b.daysLeft) as VehicleAlertItem[]
}

const quickActions = [
  {
    title: 'Siparişler',
    subtitle: 'Aktif tedarik yönetimi',
    to: '/supply',
    icon: Package,
    accent: 'bg-violet-100 text-violet-700',
  },
  {
    title: 'Yönetim',
    subtitle: 'Müşteriler, bölgeler, kişiler',
    to: '/customer-management',
    icon: Users,
    accent: 'bg-blue-100 text-blue-700',
  },
  {
    title: 'Araçlar',
    subtitle: 'Araç takip sistemi',
    to: '/vehicle-management',
    icon: Truck,
    accent: 'bg-emerald-100 text-emerald-700',
  },
  {
    title: 'Ayarlar',
    subtitle: 'Sistem yapılandırması',
    to: '/settings',
    icon: Settings,
    accent: 'bg-slate-100 text-slate-700',
  },
]

declare global {
  interface Window {
    google?: any
  }
}

const loadGoogleMapsScript = async () => {
  const key = await getGoogleMapsApiKey()
  if (!key) throw new Error('Google Maps API key missing')
  if (window.google?.maps?.places) return

  await new Promise<void>((resolve, reject) => {
    const existing = document.querySelector('script[data-google-maps="true"]') as HTMLScriptElement | null
    if (existing) {
      if (window.google?.maps) {
        resolve()
        return
      }
      const onLoad = () => resolve()
      const onError = () => reject(new Error('Google Maps script load failed'))
      existing.addEventListener('load', onLoad, { once: true })
      existing.addEventListener('error', onError, { once: true })
      return
    }

    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`
    script.async = true
    script.defer = true
    script.dataset.googleMaps = 'true'
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Google Maps script load failed'))
    document.head.appendChild(script)
  })
}

function DashboardGoogleMap({ points, routeLines, onVehicleSelect }: { points: DashboardPoint[]; routeLines: [number, number][][]; onVehicleSelect: (point: DashboardPoint) => void }) {
  const mapRef = useRef<HTMLDivElement | null>(null)
  const mapInstanceRef = useRef<any>(null)
  const overlaysRef = useRef<any[]>([])

  useEffect(() => {
    if (!mapRef.current) return

    loadGoogleMapsScript()
      .then(() => {
        if (!mapRef.current || !window.google?.maps) return

        if (!mapInstanceRef.current) {
          mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
            center: { lat: 39.0, lng: 35.0 },
            zoom: 6,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
          })
        }

        class LabelOverlay extends window.google.maps.OverlayView {
          position: any
          text: string
          active: boolean
          point: DashboardPoint
          div: HTMLDivElement | null

          constructor(position: any, text: string, active: boolean, point: DashboardPoint) {
            super()
            this.position = position
            this.text = text
            this.active = active
            this.point = point
            this.div = null
          }

          onAdd() {
            const div = document.createElement('div')
            div.style.position = 'absolute'
            div.style.transform = 'translate(-50%, -135%)'
            div.style.background = this.active ? '#16a34a' : '#2563eb'
            div.style.color = '#ffffff'
            div.style.fontSize = '12px'
            div.style.fontWeight = '700'
            div.style.padding = '4px 8px'
            div.style.borderRadius = '999px'
            div.style.whiteSpace = 'nowrap'
            div.style.boxShadow = this.active ? '0 4px 10px rgba(22,163,74,0.25)' : '0 4px 10px rgba(37,99,235,0.25)'
            div.style.border = '1px solid rgba(255,255,255,0.55)'
            div.innerText = this.text
            div.style.cursor = 'pointer'
            div.onclick = () => {
              if (this.point.type === 'vehicle') onVehicleSelect(this.point)
            }
            this.div = div
            const panes = this.getPanes()
            panes?.overlayMouseTarget?.appendChild(div)
          }

          draw() {
            if (!this.div) return
            const projection = this.getProjection()
            const pixel = projection?.fromLatLngToDivPixel(this.position)
            if (!pixel) return
            this.div.style.left = `${pixel.x}px`
            this.div.style.top = `${pixel.y}px`
          }

          onRemove() {
            if (this.div?.parentNode) {
              this.div.parentNode.removeChild(this.div)
            }
            this.div = null
          }
        }

        overlaysRef.current.forEach((overlay) => overlay.setMap(null))
        overlaysRef.current = []

        const bounds = new window.google.maps.LatLngBounds()

        routeLines.forEach((line) => {
          const polyline = new window.google.maps.Polyline({
            path: line.map(([lat, lng]) => ({ lat, lng })),
            geodesic: true,
            strokeColor: '#64748b',
            strokeOpacity: 0.6,
            strokeWeight: 2,
            map: mapInstanceRef.current,
          })
          overlaysRef.current.push(polyline)
          line.forEach(([lat, lng]) => bounds.extend({ lat, lng }))
        })

        points.forEach((point) => {
          const isMovingVehicle = point.type === 'vehicle' && ((point.speed || 0) > 0 || point.isRunning)
          const hasActiveShipment = point.type === 'vehicle' && isVehicleStatusActive(point.activeVehicleStatus)
          const color = point.type === 'pickup' ? '#f59e0b' : point.type === 'delivery' ? '#10b981' : point.type === 'vehicle' ? (hasActiveShipment || isMovingVehicle ? '#16a34a' : '#7c3aed') : '#2563eb'
          const movingVehicleSvg = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28"><circle cx="14" cy="14" r="13" fill="#16a34a" stroke="#ffffff" stroke-width="2"/><polygon points="11,8 21,14 11,20" fill="#ffffff"/></svg>`)}`
          const marker = new window.google.maps.Marker({
            position: { lat: point.latitude, lng: point.longitude },
            map: mapInstanceRef.current,
            title: `${point.label} - ${point.shipmentNo}`,
            icon: isMovingVehicle
              ? {
                  url: movingVehicleSvg,
                  scaledSize: new window.google.maps.Size(28, 28),
                }
              : {
                  path: window.google.maps.SymbolPath.CIRCLE,
                  fillColor: color,
                  fillOpacity: 1,
                  strokeColor: '#ffffff',
                  strokeWeight: 2,
                  scale: point.type === 'driver' ? 8 : point.type === 'vehicle' ? 9 : 7,
                },
          })

          if (point.type === 'vehicle') {
            const labelOverlay = new LabelOverlay({ lat: point.latitude, lng: point.longitude }, point.label, hasActiveShipment, point)
            labelOverlay.setMap(mapInstanceRef.current)
            overlaysRef.current.push(labelOverlay)
          }

          const infoWindow = new window.google.maps.InfoWindow({
            content: point.type === 'vehicle'
              ? `<div style="min-width:260px;padding:4px 2px;color:#1f2937">
                  <div style="font-weight:700;font-size:18px;margin-bottom:8px">Araç Başlığı : ${point.label}</div>
                  <div style="padding:6px 0;border-top:1px solid #e5e7eb"><b>Aktif Tedarik :</b> ${point.activeShipmentId ? `<a href="/supply?shipment=${point.activeShipmentId}" style="color:#2563eb;text-decoration:none;font-weight:600">${point.activeShipmentNo || '-'}</a>` : '-'}</div>
                  <div style="padding:6px 0;border-top:1px solid #e5e7eb"><b>Araç Durum :</b> ${point.activeVehicleStatus || '-'}</div>
                  <div style="padding:6px 0;border-top:1px solid #e5e7eb"><b>Hat :</b> ${(point.activePickupName || '-') + ' → ' + (point.activeDeliveryName || '-')}</div>
                  <div style="padding:6px 0;border-top:1px solid #e5e7eb"><b>Plaka :</b> ${point.label}</div>
                  <div style="padding:6px 0;border-top:1px solid #e5e7eb"><b>Treyler Plaka :</b> ${(point.subtitle || '').split(' • ')[1] || '-'}</div>
                  <div style="padding:6px 0;border-top:1px solid #e5e7eb"><b>Sürücü/Asil :</b> ${(point.subtitle || '').split(' • ')[0] || '-'}</div>
                  <div style="padding:6px 0;border-top:1px solid #e5e7eb"><b>Günlük Km :</b> ${point.dailyKm != null ? point.dailyKm.toLocaleString('tr-TR') + ' km' : '-'}</div>
                  <div style="padding:6px 0;border-top:1px solid #e5e7eb"><b>Günlük Tüketim :</b> ${point.dailyFuel != null ? point.dailyFuel.toLocaleString('tr-TR') + ' lt' : '-'}</div>
                  <div style="padding:6px 0;border-top:1px solid #e5e7eb"><b>Depo Seviyesi :</b> ${point.fuelLevel != null ? '%' + point.fuelLevel.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</div>
                  <div style="padding:6px 0;border-top:1px solid #e5e7eb"><b>Kontak Açıldı :</b> ${point.ignitionOn || '-'}</div>
                  <div style="padding:6px 0;border-top:1px solid #e5e7eb"><b>Kontak Kapandı :</b> ${point.ignitionOff || '-'}</div>
                  <div style="padding:6px 0;border-top:1px solid #e5e7eb"><b>Günlük Rölanti :</b> ${point.dailyIdleTime || '-'}</div>
                  <div style="padding:6px 0;border-top:1px solid #e5e7eb"><b>Günlük Çalışma :</b> ${point.dailyWorkTime || '-'}</div>
                  <div style="padding:6px 0;border-top:1px solid #e5e7eb"><b>Günlük Sürüş :</b> ${point.dailyDriveTime || '-'}</div>
                </div>`
              : `<div style="min-width:180px;padding:4px 2px"><div style="font-weight:600;color:#0f172a">${point.label}</div><div style="font-size:12px;color:#64748b">Sevk: ${point.shipmentNo}</div>${point.subtitle ? `<div style="font-size:12px;color:#64748b">${point.subtitle}</div>` : ''}</div>`,
          })

          if (point.type === 'vehicle') {
            marker.addListener('click', () => onVehicleSelect(point))
          } else {
            marker.addListener('click', () => infoWindow.open({ map: mapInstanceRef.current, anchor: marker }))
            marker.addListener('mouseover', () => infoWindow.open({ map: mapInstanceRef.current, anchor: marker }))
            marker.addListener('mouseout', () => infoWindow.close())
          }
          overlaysRef.current.push(marker)
          bounds.extend({ lat: point.latitude, lng: point.longitude })
        })

        if (!bounds.isEmpty()) {
          mapInstanceRef.current.fitBounds(bounds, 32)
        }
      })
      .catch(() => {
        // Fallback UI is rendered by parent when needed.
      })
  }, [points, routeLines, onVehicleSelect])

  if (!getGoogleMapsApiKeySync()) {
    return <div className="flex h-full items-center justify-center bg-slate-50 text-slate-400">Google Maps API key tanımlı değil</div>
  }

  return <div ref={mapRef} className="h-full w-full" />
}

const toCurrency = (value: number) =>
  value.toLocaleString('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2,
  })

const haversineKm = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const toRad = (value: number) => (value * Math.PI) / 180
  const earthRadius = 6371
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return earthRadius * c
}

const buildInitialWizardForm = (): WizardForm => ({
  tripType: 'parsiyel',
  pickupOptionId: '',
  deliveryOptionId: '',
  pickupLabel: '',
  pickupCity: '',
  pickupDistrict: '',
  pickupAddress: '',
  deliveryLabel: '',
  deliveryCity: '',
  deliveryDistrict: '',
  deliveryAddress: '',
  pickupLatitude: null,
  pickupLongitude: null,
  deliveryLatitude: null,
  deliveryLongitude: null,
  vehicleType: [],
  loadType: '',
  weightKg: 0,
  volumeM3: 0,
  palletCount: 1,
  vehicleCount: 1,
  distanceKm: 0,
  notes: '',
  marginPercent: 15,
})

const buildInitialCustomerForm = (): CustomerCreateForm => ({
  companyCode: '',
  companyName: '',
  taxNumber: '',
  taxOffice: '',
  contactPerson: '',
  phone: '',
  email: '',
  paymentType: '30',
  address: '',
  city: '',
  district: '',
  latitude: null,
  longitude: null,
})

export function Dashboard() {
  const { user } = useAuth()
  const roleInfo = user ? ROLE_INFO[user.role] : null
  const [overview, setOverview] = useState<DashboardOverview | null>(null)
  const overviewRequestRef = useRef<AbortController | null>(null)
  const [loading, setLoading] = useState(true)
  const [financeTab, setFinanceTab] = useState<'finance' | 'orders'>('finance')
  const [wizardOpen, setWizardOpen] = useState(false)
  const [supplyModalOpen, setSupplyModalOpen] = useState(false)
  const [wizardStep, setWizardStep] = useState<WizardStep>(1)
  const [wizardSubmitting, setWizardSubmitting] = useState(false)
  const [wizardForm, setWizardForm] = useState<WizardForm>(buildInitialWizardForm())
  const [routeOptions, setRouteOptions] = useState<RouteOption[]>([])
  const [pickupSearch, setPickupSearch] = useState('')
  const [deliverySearch, setDeliverySearch] = useState('')
  const [customerModalOpen, setCustomerModalOpen] = useState(false)
  const [customerModalTarget, setCustomerModalTarget] = useState<'pickup' | 'delivery'>('pickup')
  const [customerSubmitting, setCustomerSubmitting] = useState(false)
  const [customerForm, setCustomerForm] = useState<CustomerCreateForm>(buildInitialCustomerForm())
  const customerAddressInputRef = useRef<HTMLInputElement | null>(null)
  const customerAutocompleteRef = useRef<any>(null)
  const [intermediateStops, setIntermediateStops] = useState<IntermediateStop[]>([])
  const [stopSearch, setStopSearch] = useState('')
  const [showStopPicker, setShowStopPicker] = useState(false)
  const [draggingStopId, setDraggingStopId] = useState<string | null>(null)
  const [basePrices, setBasePrices] = useState<any[]>([])
  const [distanceEdited, setDistanceEdited] = useState(false)
  const [supportUnreadCount, setSupportUnreadCount] = useState(0)
  const [openIncidentCount, setOpenIncidentCount] = useState(0)
  const [openIncidentBreakdown, setOpenIncidentBreakdown] = useState<Array<{ plate: string; count: number }>>([])
  const [insuranceAlerts, setInsuranceAlerts] = useState<VehicleAlertItem[]>([])
  const [inspectionAlerts, setInspectionAlerts] = useState<VehicleAlertItem[]>([])
  const [maintenanceAlerts, setMaintenanceAlerts] = useState<VehicleAlertItem[]>([])
  const [seyirViolations, setSeyirViolations] = useState<SeyirViolationRow[]>([])
  const [seyirViolationModalOpen, setSeyirViolationModalOpen] = useState(false)
  const [unusedDigitalLicenseModalOpen, setUnusedDigitalLicenseModalOpen] = useState(false)
  const [unusedDigitalLicenseDrivers, setUnusedDigitalLicenseDrivers] = useState<UnusedDigitalLicenseDriver[]>([])
  const [unusedDigitalLicenseLoading, setUnusedDigitalLicenseLoading] = useState(false)
  const [newSpotDrivers, setNewSpotDrivers] = useState<NewSpotDriversData | null>(null)
  const [newSpotDriversModalOpen, setNewSpotDriversModalOpen] = useState(false)
  const [newSpotDriversTab, setNewSpotDriversTab] = useState<'today' | 'week' | 'month'>('today')
  const [vehicleAlertModalType, setVehicleAlertModalType] = useState<VehicleAlertModalType>(null)
  const [overdueSpotInvoiceModalOpen, setOverdueSpotInvoiceModalOpen] = useState(false)
  const [overdueOwnFleetDeliveryModalOpen, setOverdueOwnFleetDeliveryModalOpen] = useState(false)
  const topCityMaxCount = Math.max(...((overview?.topOwnFleetVehicleCities || []).map((item) => item.count)), 1)

  const loadRouteOptions = async () => {
    try {
      const [customersResponse, warehousesResponse] = await Promise.all([
        customerService.getCustomers({ status: 'active' }),
        warehouseService.getWarehouses(),
      ])

      const customerOptions = (customersResponse.data || []).map((item: any) => ({
        id: `customer-${item.id}`,
        type: 'customer' as const,
        label: item.company_name || item.name,
        city: item.city || '',
        district: item.district || '',
        address: item.address || '',
        latitude: item.latitude ? Number(item.latitude) : null,
        longitude: item.longitude ? Number(item.longitude) : null,
      }))

      const warehouseOptions = (warehousesResponse.data || []).map((item: any) => ({
        id: `warehouse-${item.id}`,
        type: 'warehouse' as const,
        label: item.name,
        city: item.city || '',
        district: item.district || '',
        address: item.address || '',
        latitude: item.latitude ? Number(item.latitude) : null,
        longitude: item.longitude ? Number(item.longitude) : null,
      }))

      setRouteOptions([...customerOptions, ...warehouseOptions])
    } catch {
      setRouteOptions([])
    }
  }

  const refreshOverview = async () => {
    overviewRequestRef.current?.abort()
    const requestController = new AbortController()
    overviewRequestRef.current = requestController
    setLoading(true)
    try {
      const [overviewResponse, supportResponse, incidentResponse, vehiclesResponse, seyirViolationResponse, newSpotDriversResponse] = await Promise.all([
        dashboardService.getOverview(requestController.signal),
        supportService.getThreads().catch(() => ({ data: [] })),
        driverIncidentReportService.getReports().catch(() => ({ data: [] })),
        vehicleService.getVehicles().catch(() => ({ data: [] })),
        seyirViolationService.getViolations({ limit: 1000 }).catch(() => ({ data: [] })),
        dashboardService.getNewSpotDrivers().catch(() => ({ data: null })),
      ])

      if (overviewResponse.success && overviewResponse.data) {
        setOverview(overviewResponse.data as DashboardOverview)
      }
      if ((newSpotDriversResponse as any)?.success && (newSpotDriversResponse as any)?.data) {
        setNewSpotDrivers((newSpotDriversResponse as any).data)
      }

      const supportThreads = Array.isArray((supportResponse as any)?.data)
        ? (supportResponse as any).data
        : Array.isArray(supportResponse)
          ? supportResponse
          : []
      setSupportUnreadCount(supportThreads.reduce((sum: number, thread: any) => sum + Number(thread.unreadCount || 0), 0))

      const incidentRows = Array.isArray((incidentResponse as any)?.data)
        ? (incidentResponse as any).data
        : []
      const openIncidentRows = incidentRows.filter((item: any) => String(item.workflowStatus || item.status || '') !== 'completed')
      setOpenIncidentCount(openIncidentRows.length)
      const breakdownMap = openIncidentRows.reduce((acc: Record<string, number>, item: any) => {
        const plate = String(item.vehicleKind === 'trailer' ? (item.trailerPlate || '-') : (item.vehiclePlate || '-'));
        acc[plate] = (acc[plate] || 0) + 1
        return acc
      }, {})
      setOpenIncidentBreakdown(Object.entries(breakdownMap).map(([plate, count]) => ({ plate, count })).sort((a, b) => b.count - a.count).slice(0, 4))
      const vehicleRows = Array.isArray((vehiclesResponse as any)?.data)
        ? (vehiclesResponse as any).data
        : Array.isArray(vehiclesResponse)
          ? vehiclesResponse
          : []
      setInsuranceAlerts(buildVehicleAlertItems(vehicleRows, 'insurance_expiry', 'Sigorta'))
      setInspectionAlerts(buildVehicleAlertItems(vehicleRows, 'license_expiry', 'Muayene'))
      setMaintenanceAlerts(buildVehicleAlertItems(vehicleRows, 'next_maintenance_date', 'Bakım'))
      setSeyirViolations(Array.isArray((seyirViolationResponse as any)?.data) ? (seyirViolationResponse as any).data : [])
    } catch (error) {
      if (requestController.signal.aborted) return
      toast.error('Dashboard verileri yüklenemedi')
    } finally {
      if (overviewRequestRef.current === requestController) setLoading(false)
      setLoading(false)
    }
  }

  const openUnusedDigitalLicenseModal = async () => {
    setUnusedDigitalLicenseModalOpen(true)
    setUnusedDigitalLicenseLoading(true)
    try {
      const response: any = await dashboardService.getUnusedDigitalLicenseDrivers(72)
      setUnusedDigitalLicenseDrivers(Array.isArray(response?.data) ? response.data : [])
    } catch (error) {
      console.error('Unused digital license drivers load failed:', error)
      toast.error('Kullanılmayan dijital ehliyet listesi alınamadı')
      setUnusedDigitalLicenseDrivers([])
    } finally {
      setUnusedDigitalLicenseLoading(false)
    }
  }

  useEffect(() => {
    refreshOverview()
    return () => overviewRequestRef.current?.abort()
  }, [])

  useAutoRefresh(() => refreshOverview(), { intervalMs: 30000 })

  useEffect(() => {
    loadRouteOptions()
  }, [])

  useEffect(() => {
    const loadBasePrices = async () => {
      try {
        const response = await basePriceService.getAll()
        setBasePrices(response.data || [])
      } catch {
        setBasePrices([])
      }
    }
    loadBasePrices()
  }, [])

  useEffect(() => {
    if (!customerModalOpen || !customerAddressInputRef.current) return

    let cancelled = false

    loadGoogleMapsScript()
      .then(() => {
        if (cancelled || !customerAddressInputRef.current || !window.google?.maps?.places) return

        const autocomplete = new window.google.maps.places.Autocomplete(customerAddressInputRef.current, {
          fields: ['formatted_address', 'geometry', 'address_components', 'name'],
          componentRestrictions: { country: 'tr' },
        })

        customerAutocompleteRef.current = autocomplete

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace()
          const components = place?.address_components || []
          const getPart = (type: string) => components.find((component: any) => component.types.includes(type))?.long_name || ''
          const city = getPart('administrative_area_level_1') || getPart('locality')
          const district = getPart('administrative_area_level_2') || getPart('sublocality_level_1')
          const latitude = place?.geometry?.location?.lat ? Number(place.geometry.location.lat()) : null
          const longitude = place?.geometry?.location?.lng ? Number(place.geometry.location.lng()) : null

          setCustomerForm((current) => ({
            ...current,
            address: place?.formatted_address || place?.name || current.address,
            city: city || current.city,
            district: district || current.district,
            latitude,
            longitude,
          }))
        })
      })
      .catch(() => {
        toast.error('Google Places yüklenemedi')
      })

    return () => {
      cancelled = true
      customerAutocompleteRef.current = null
    }
  }, [customerModalOpen])

  const totalFleetVehicles = overview?.metrics.totalFleetVehicles || 0
  const availableVehicles = overview?.metrics.availableVehicles || 0
  const availableVehiclePercent = totalFleetVehicles > 0 ? Math.round((availableVehicles / totalFleetVehicles) * 100) : 0
  const showDashboardLoading = loading && !overview
  const vehicleAlertMap: Record<Exclude<VehicleAlertModalType, null>, { title: string; items: VehicleAlertItem[]; accent: string }> = {
    insurance: { title: '30 Gün İçinde Sigortası Bitecekler', items: insuranceAlerts, accent: 'text-rose-600' },
    inspection: { title: '30 Gün İçinde Muayenesi Bitecekler', items: inspectionAlerts, accent: 'text-amber-600' },
    maintenance: { title: 'Bakımı Yaklaşanlar', items: maintenanceAlerts, accent: 'text-sky-600' },
  }
  const activeVehicleAlert = vehicleAlertModalType ? vehicleAlertMap[vehicleAlertModalType] : null
  const exportVehicleAlerts = () => {
    if (!activeVehicleAlert) return
    exportRowsToExcel(activeVehicleAlert.items, `dashboard-${vehicleAlertModalType || 'vehicle-alerts'}-${new Date().toISOString().slice(0, 10)}.xlsx`, {
      sheetName: activeVehicleAlert.title,
      columns: [
        { key: 'plate', label: 'Çekici' },
        { key: 'trailerPlate', label: 'Dorse' },
        { key: 'date', label: 'Tarih', value: (row) => formatDateTimeTr(row.date) },
        { key: 'daysLeft', label: 'Kalan Gün' },
      ],
    })
  }

  const exportSeyirViolations = () => {
    exportRowsToExcel(seyirViolations, `seyir-ehliyet-ihlalleri-${new Date().toISOString().slice(0, 10)}.xlsx`, {
      sheetName: 'SeyirEhliyetIhlalleri',
      columns: [
        { key: 'first_detected_at', label: 'İlk Tespit', value: (row) => formatDateTimeTr(row.first_detected_at) },
        { key: 'last_detected_at', label: 'Son Tespit', value: (row) => formatDateTimeTr(row.last_detected_at) },
        { key: 'check_count', label: 'Tekrar' },
        { key: 'violation_type', label: 'İhlal Tipi', value: (row) => getSeyirViolationLabel(row.violation_type) },
        { key: 'tractor_plate', label: 'Çekici', value: (row) => row.tractor_plate || '-' },
        { key: 'trailer_plate', label: 'Dorse', value: (row) => row.trailer_plate || '-' },
        { key: 'system_driver_name', label: 'Araç Sürücüsü', value: (row) => row.system_driver_name || '-' },
        { key: 'system_driver_license_no', label: 'Araç Ehliyet No', value: (row) => row.system_driver_license_no || '-' },
        { key: 'seyir_digital_license_no', label: 'Seyir Ehliyet No', value: (row) => row.seyir_digital_license_no || '-' },
        { key: 'seyir_driver_name', label: 'Seyir Sürücü', value: (row) => row.seyir_driver_name || '-' },
        { key: 'matched_driver_name', label: 'Eşleşen Özmal Sürücü', value: (row) => row.matched_driver_name || '-' },
      ],
    })
  }

  const estimatedDistanceKm = useMemo(() => {
    const routePoints = [
      {
        latitude: wizardForm.pickupLatitude,
        longitude: wizardForm.pickupLongitude,
      },
      ...intermediateStops.map((stop) => ({ latitude: stop.latitude ?? null, longitude: stop.longitude ?? null })),
      {
        latitude: wizardForm.deliveryLatitude,
        longitude: wizardForm.deliveryLongitude,
      },
    ]

    let total = 0
    for (let i = 0; i < routePoints.length - 1; i += 1) {
      const current = routePoints[i]
      const next = routePoints[i + 1]
      if (current.latitude == null || current.longitude == null || next.latitude == null || next.longitude == null) {
        return 0
      }
      total += haversineKm(current.latitude, current.longitude, next.latitude, next.longitude)
    }

    return Number(total.toFixed(1))
  }, [wizardForm.pickupLatitude, wizardForm.pickupLongitude, wizardForm.deliveryLatitude, wizardForm.deliveryLongitude, intermediateStops])

  useEffect(() => {
    if (!distanceEdited) {
      setWizardForm((current) => ({ ...current, distanceKm: estimatedDistanceKm }))
    }
  }, [estimatedDistanceKm, distanceEdited])

  const regionalBasePrice = useMemo(() => {
    if (!wizardForm.pickupOptionId.startsWith('warehouse-')) return 0
    const warehouseId = Number(wizardForm.pickupOptionId.replace('warehouse-', ''))
    const deliveryDistrict = wizardForm.deliveryDistrict?.trim().toLocaleLowerCase('tr-TR')
    const deliveryCity = wizardForm.deliveryCity?.trim().toLocaleLowerCase('tr-TR')
    if (!warehouseId || !deliveryCity) return 0

    const today = new Date().toISOString().slice(0, 10)
    const activeBasePrices = basePrices.filter((item) => {
      const validFrom = String(item.valid_from_str || item.valid_from || '').slice(0, 10)
      return !validFrom || validFrom <= today
    })

    const exact = activeBasePrices.find((item) => Number(item.warehouse_id) === warehouseId && String(item.delivery_city || '').trim().toLocaleLowerCase('tr-TR') === deliveryCity && String(item.delivery_district || '').trim().toLocaleLowerCase('tr-TR') === deliveryDistrict)
    if (exact) return Number(exact.base_price || 0)
    const cityMatch = activeBasePrices.find((item) => Number(item.warehouse_id) === warehouseId && String(item.delivery_city || '').trim().toLocaleLowerCase('tr-TR') === deliveryCity)
    return Number(cityMatch?.base_price || 0)
  }, [basePrices, wizardForm.pickupOptionId, wizardForm.deliveryCity, wizardForm.deliveryDistrict])

  const effectiveDistanceKm = distanceEdited ? Number(wizardForm.distanceKm || 0) : estimatedDistanceKm

  const estimatedBaseCost = useMemo(() => {
    const distanceCost = effectiveDistanceKm * (wizardForm.tripType === 'komple' ? 24 : 18)
    const weightCost = wizardForm.weightKg * 0.18
    const palletCost = wizardForm.palletCount * 35
    const vehicleMultiplier = Math.max(1, wizardForm.vehicleCount)
    const baseCost = regionalBasePrice > 0 ? regionalBasePrice : distanceCost
    return Number(((baseCost + weightCost + palletCost) * vehicleMultiplier).toFixed(2))
  }, [effectiveDistanceKm, regionalBasePrice, wizardForm.tripType, wizardForm.weightKg, wizardForm.palletCount, wizardForm.vehicleCount])

  const estimatedOffer = useMemo(() => {
    return Number((estimatedBaseCost * (1 + wizardForm.marginPercent / 100)).toFixed(2))
  }, [estimatedBaseCost, wizardForm.marginPercent])

  const updateRouteSelection = (field: 'pickup' | 'delivery', optionId: string) => {
    const option = routeOptions.find((item) => item.id === optionId)
    if (!option) return

    if (field === 'pickup') {
      setWizardForm((current) => ({
        ...current,
        pickupOptionId: option.id,
        pickupLabel: option.label,
        pickupCity: option.city || '',
        pickupDistrict: option.district || '',
        pickupAddress: option.address || '',
        pickupLatitude: option.latitude ?? null,
        pickupLongitude: option.longitude ?? null,
      }))
      return
    }

    setWizardForm((current) => ({
      ...current,
      deliveryOptionId: option.id,
      deliveryLabel: option.label,
      deliveryCity: option.city || '',
      deliveryDistrict: option.district || '',
      deliveryAddress: option.address || '',
      deliveryLatitude: option.latitude ?? null,
      deliveryLongitude: option.longitude ?? null,
    }))
    setDeliverySearch(`${option.label}${option.city ? ` - ${option.city}` : ''}`)
  }

  const openCustomerModal = (target: 'pickup' | 'delivery') => {
    setCustomerModalTarget(target)
    setCustomerForm(buildInitialCustomerForm())
    setCustomerModalOpen(true)
  }

  const saveCustomerAndSelect = async (event?: React.FormEvent) => {
    event?.preventDefault()
    if (!customerForm.companyName.trim() || !customerForm.phone.trim() || !customerForm.city.trim()) {
      toast.error('Firma adı, telefon ve il zorunlu')
      return
    }

    setCustomerSubmitting(true)
    try {
      const response = await customerService.createCustomer({
        companyCode: customerForm.companyCode || undefined,
        companyName: customerForm.companyName,
        taxNumber: customerForm.taxNumber || undefined,
        contactPerson: customerForm.contactPerson || undefined,
        email: customerForm.email || undefined,
        phone: customerForm.phone,
        address: customerForm.address || undefined,
        city: customerForm.city,
        district: customerForm.district || undefined,
        latitude: customerForm.latitude,
        longitude: customerForm.longitude,
        paymentTerms: Number(customerForm.paymentType || 30),
        customerType: 'Her İkisi',
      })

      const newId = response?.data?.id
      const newOption = {
        id: `customer-${newId}`,
        type: 'customer' as const,
        label: customerForm.companyName,
        city: customerForm.city,
        district: customerForm.district,
        address: customerForm.address,
        latitude: customerForm.latitude,
        longitude: customerForm.longitude,
      }

      setRouteOptions((current) => {
        const next = current.filter((item) => item.id !== newOption.id)
        return [newOption, ...next]
      })

      if (customerModalTarget === 'pickup') {
        setWizardForm((current) => ({
          ...current,
          pickupOptionId: newOption.id,
          pickupLabel: newOption.label,
          pickupCity: newOption.city,
          pickupDistrict: newOption.district,
          pickupAddress: newOption.address,
          pickupLatitude: newOption.latitude,
          pickupLongitude: newOption.longitude,
        }))
        setPickupSearch(`${newOption.label}${newOption.city ? ` - ${newOption.city}` : ''}`)
      } else {
        setWizardForm((current) => ({
          ...current,
          deliveryOptionId: newOption.id,
          deliveryLabel: newOption.label,
          deliveryCity: newOption.city,
          deliveryDistrict: newOption.district,
          deliveryAddress: newOption.address,
          deliveryLatitude: newOption.latitude,
          deliveryLongitude: newOption.longitude,
        }))
        setDeliverySearch(`${newOption.label}${newOption.city ? ` - ${newOption.city}` : ''}`)
      }
      setCustomerModalOpen(false)
      toast.success('Müşteri kaydedildi ve seçildi')
    } catch (error) {
      console.error('Save customer from dashboard modal error:', error)
      toast.error('Müşteri kaydedilemedi')
    } finally {
      setCustomerSubmitting(false)
    }
  }

  const filteredPickupOptions = routeOptions.filter((option) => {
    const query = pickupSearch.trim().toLocaleLowerCase('tr-TR')
    if (!query) return true
    return `${option.label} ${option.city || ''} ${option.district || ''} ${option.address || ''}`.toLocaleLowerCase('tr-TR').includes(query)
  })

  const filteredDeliveryOptions = routeOptions.filter((option) => {
    const query = deliverySearch.trim().toLocaleLowerCase('tr-TR')
    if (!query) return true
    return `${option.label} ${option.city || ''} ${option.district || ''} ${option.address || ''}`.toLocaleLowerCase('tr-TR').includes(query)
  })

  const filteredStopOptions = routeOptions.filter((option) => {
    const query = stopSearch.trim().toLocaleLowerCase('tr-TR')
    if (!query) return true
    if (option.id === wizardForm.pickupOptionId || option.id === wizardForm.deliveryOptionId) return false
    if (intermediateStops.some((stop) => stop.id === option.id)) return false
    return `${option.label} ${option.city || ''} ${option.district || ''} ${option.address || ''}`.toLocaleLowerCase('tr-TR').includes(query)
  })

  const closeWizard = () => {
    setWizardOpen(false)
    setWizardStep(1)
    setWizardForm(buildInitialWizardForm())
    setIntermediateStops([])
    setPickupSearch('')
    setDeliverySearch('')
    setStopSearch('')
    setShowStopPicker(false)
  }

  const addIntermediateStop = (optionId: string) => {
    const option = routeOptions.find((item) => item.id === optionId)
    if (!option) return
    setIntermediateStops((current) => [
      ...current,
      {
        id: option.id,
        label: option.label,
        city: option.city,
        district: option.district,
        address: option.address,
        latitude: option.latitude ?? null,
        longitude: option.longitude ?? null,
      },
    ])
    setStopSearch('')
    setShowStopPicker(false)
  }

  const removeIntermediateStop = (stopId: string) => {
    setIntermediateStops((current) => current.filter((stop) => stop.id !== stopId))
  }

  const moveIntermediateStop = (sourceId: string, targetId: string) => {
    if (sourceId === targetId) return
    setIntermediateStops((current) => {
      const sourceIndex = current.findIndex((stop) => stop.id === sourceId)
      const targetIndex = current.findIndex((stop) => stop.id === targetId)
      if (sourceIndex === -1 || targetIndex === -1) return current
      const next = [...current]
      const [moved] = next.splice(sourceIndex, 1)
      next.splice(targetIndex, 0, moved)
      return next
    })
  }

  const validateStep = (step: WizardStep) => {
    if (step === 1) {
      if (!wizardForm.pickupOptionId || !wizardForm.deliveryOptionId) {
        toast.error('Başlangıç ve bitiş noktalarını seçin')
        return false
      }
      if (!wizardForm.vehicleType?.length) {
        toast.error('Araç tipi seçin')
        return false
      }
      return true
    }

    if (step === 2) {
      if (!wizardForm.loadType.trim()) {
        toast.error('Yük türünü girin')
        return false
      }
      if (wizardForm.weightKg <= 0) {
        toast.error('Yük ağırlığı 0’dan büyük olmalı')
        return false
      }
      if (wizardForm.vehicleCount <= 0) {
        toast.error('Araç adedi en az 1 olmalı')
        return false
      }
      return true
    }

    return true
  }

  const handleNextStep = () => {
    if (!validateStep(wizardStep)) return
    setWizardStep((current) => Math.min(3, current + 1) as WizardStep)
  }

  const handleStepClick = (step: WizardStep) => {
    if (step <= wizardStep) {
      setWizardStep(step)
      return
    }

    for (let current = 1 as WizardStep; current < step; current = (current + 1) as WizardStep) {
      if (!validateStep(current)) return
    }

    setWizardStep(step)
  }

  const submitWizard = async () => {
    if (!wizardForm.pickupLabel || !wizardForm.deliveryLabel || !wizardForm.vehicleType?.length) {
      toast.error('Yükleme, teslimat ve araç tipi zorunlu')
      return
    }

    setWizardSubmitting(true)
    try {
      await supplyService.create({
        durum: 'teklif-topla',
        aracTipi: 'Spot',
        aracTuru: wizardForm.vehicleType.join(', '),
        yukTipi: wizardForm.loadType,
        yukAgirlik: wizardForm.weightKg,
        yukHacim: wizardForm.volumeM3,
        palletCount: wizardForm.palletCount,
        vehicleCount: wizardForm.vehicleCount,
        stopCount: Math.max(1, intermediateStops.length + 1),
        mesafe: effectiveDistanceKm,
        maliyet: estimatedBaseCost,
        yuklemeMusterisiAd: wizardForm.pickupLabel,
        yuklemeIl: wizardForm.pickupCity,
        yuklemeIlce: wizardForm.pickupDistrict,
        yuklemeAdres: wizardForm.pickupAddress,
        teslimMusterisiAd: wizardForm.deliveryLabel,
        teslimIl: wizardForm.deliveryCity,
        teslimIlce: wizardForm.deliveryDistrict,
        teslimAdres: wizardForm.deliveryAddress,
        pickupLatitude: wizardForm.pickupLatitude,
        pickupLongitude: wizardForm.pickupLongitude,
        deliveryLatitude: wizardForm.deliveryLatitude,
        deliveryLongitude: wizardForm.deliveryLongitude,
        siparisKodu: String(Date.now()),
        notlar: [
          wizardForm.notes || '',
          intermediateStops.length > 0 ? `Ara Noktalar: ${intermediateStops.map((stop) => stop.label).join(', ')}` : '',
        ].filter(Boolean).join(' | ') || null,
      })

      toast.success('Sevk siparişi açıldı')
      closeWizard()
      refreshOverview()
    } catch {
      toast.error('Sevk siparişi oluşturulamadı')
    } finally {
      setWizardSubmitting(false)
    }
  }

  const submitSupplyModal = async (payload: any) => {
    await supplyService.create(payload)
    toast.success('Sevk siparişi açıldı')
    setSupplyModalOpen(false)
    refreshOverview()
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">Dashboard</h1>
          <p className="mt-2 text-sm text-slate-500">Operasyon, tedarik ve finansal özeti tek ekranda izleyin.</p>
        </div>
        {roleInfo && (
          <div className={`inline-flex items-center rounded-full border px-4 py-2 text-sm font-medium ${roleInfo.color}`}>
            {roleInfo.label}
          </div>
        )}
      </div>

      <section className="rounded-2xl border border-emerald-100 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500 text-white shadow-lg shadow-emerald-200">
              <Zap className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">{overview?.company?.name || 'Doğuş Çay'}</h2>
              <p className="text-sm text-slate-500">{overview?.company?.subtitle || 'Filo ve operasyon yönetimi'}</p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setWizardOpen(true)}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-200 transition hover:bg-emerald-600"
          >
            <Wallet className="h-4 w-4" />
            Sevk Siparişi Aç
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <button type="button" onClick={() => setSeyirViolationModalOpen(true)} className="rounded-2xl border border-rose-200 bg-white p-5 text-left shadow-sm transition hover:shadow-md">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-rose-50 text-rose-600">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">Seyir Ehliyet İhlalleri</p>
                <p className="mt-1 text-sm text-slate-500">Dijital ehliyet uyumsuzluk geçmişi</p>
              </div>
            </div>
            <p className="text-3xl font-bold text-rose-600">
              {showDashboardLoading ? <DashboardPulseValue className="h-8 w-12" /> : seyirViolations.length}
            </p>
          </div>
          <div className="mt-6 flex items-center justify-between text-xs text-slate-400">
            <span>{seyirViolations.length > 0 ? 'Geçmiş detay listesi için tıklayın' : 'İhlal kaydı yok'}</span>
            <span className="inline-flex items-center gap-1 text-slate-500">Detayları gör<ArrowRight className="h-3 w-3" /></span>
          </div>
        </button>
        <button type="button" onClick={() => void openUnusedDigitalLicenseModal()} className="rounded-2xl border-2 border-violet-300 bg-violet-50 p-5 text-left shadow-sm transition hover:border-violet-400 hover:shadow-md">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-violet-900">72 Saatten Fazla Kullanılmayan Dijital Ehliyetler</p>
              <p className="mt-1 text-sm text-violet-700">Özmal sürücülerinin kart kullanım takibi</p>
            </div>
            <Clock3 className="h-5 w-5 text-violet-600" />
          </div>
          <div className="mt-4 text-3xl font-semibold text-violet-600">{showDashboardLoading ? <DashboardPulseValue className="h-10 w-20" /> : (overview?.metrics.unusedDigitalLicenseDrivers72h || 0)}</div>
          <p className="mt-2 text-xs text-violet-700">Sürücü listesini görmek için tıklayın</p>
        </button>
        <button type="button" onClick={() => setNewSpotDriversModalOpen(true)} className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-5 text-left shadow-sm transition hover:border-emerald-300 hover:shadow-md">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-emerald-950">Yeni Spot Sürücüler</p>
              <p className="mt-1 text-sm text-emerald-700">Dönemsel yeni sürücü kayıtları</p>
            </div>
            <Users className="h-5 w-5 text-emerald-600" />
          </div>
          <div className="mt-4 flex items-end gap-3 text-emerald-700">
            <span className="text-3xl font-semibold">{showDashboardLoading ? <DashboardPulseValue className="h-10 w-16" /> : (newSpotDrivers?.counts.today || 0)}</span>
            <span className="pb-1 text-xs font-medium">bugün</span>
          </div>
          <div className="mt-3 flex gap-3 text-xs text-emerald-800">
            <span>Hafta: {newSpotDrivers?.counts.week || 0}</span>
            <span>Ay: {newSpotDrivers?.counts.month || 0}</span>
          </div>
          <p className="mt-2 text-xs text-emerald-700">Kayıt listesini görmek için tıklayın</p>
        </button>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
                <Truck className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">Müsait Araç</p>
                <p className="mt-1 text-sm text-slate-500">Özmal filoda şu an boşta olan çekiciler</p>
              </div>
            </div>
            <p className="text-xl font-bold text-amber-500">
              {showDashboardLoading ? <DashboardPulseValue className="h-7 w-28" /> : `%${availableVehiclePercent} (${availableVehicles}/${totalFleetVehicles || 0})`}
            </p>
          </div>
          <div className="mt-6 h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-amber-400" style={{ width: `${availableVehiclePercent}%` }} /></div>
          <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
            <span>{totalFleetVehicles > 0 ? `${availableVehicles} araç müsait` : 'Aktif filo verisi yok'}</span>
            <Link to="/vehicle-management" className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-700">Detayları gör<ArrowRight className="h-3 w-3" /></Link>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-rose-50 text-rose-600">
                <Package className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">Aktif Operasyon</p>
                <p className="mt-1 text-sm text-slate-500">Açık, atanmış veya süreçte olan sevkiyatlar</p>
              </div>
            </div>
            <div className="text-xl font-bold text-rose-500">{showDashboardLoading ? <DashboardPulseValue className="h-7 w-24" /> : `${overview?.metrics.activeShipments || 0} sevkiyat`}</div>
          </div>
          <div className="mt-6 flex items-center justify-between text-xs text-slate-400">
            <span>{showDashboardLoading ? <DashboardPulseValue className="h-4 w-28" /> : `Plaka atanmayan: ${overview?.metrics.unassignedOrders || 0}`}</span>
            <Link to="/supply" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">Detayları gör<ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                <MapPinned className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">Teslim Onayı Bekleyen</p>
                <p className="mt-1 text-sm text-slate-500">Teslim evrağı yüklendi, operasyon onayı bekleniyor</p>
              </div>
            </div>
            <div className="text-xl font-bold text-sky-600">{showDashboardLoading ? <DashboardPulseValue className="h-7 w-24" /> : `${overview?.metrics.deliveryApprovalPending || 0} sevkiyat`}</div>
          </div>
          <div className="mt-6 flex items-center justify-between text-xs text-slate-400">
            <span>{showDashboardLoading ? <DashboardPulseValue className="h-4 w-36" /> : `Yüklemede: ${overview?.metrics.loadingVehicles || 0} | Yolda: ${overview?.metrics.transportVehicles || 0}`}</span>
            <Link to="/accounting-workflow" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">Detayları gör<ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
        <button type="button" onClick={() => setVehicleAlertModalType('insurance')} className="rounded-2xl border border-rose-200 bg-white p-5 text-left shadow-sm transition hover:shadow-md">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">30 Gün İçinde Sigortası Bitecekler</p>
              <p className="mt-1 text-sm text-slate-500">Özmal araç sigorta bitiş uyarıları</p>
            </div>
            <AlertTriangle className="h-5 w-5 text-rose-500" />
          </div>
          <p className="mt-4 text-3xl font-semibold text-rose-600">{insuranceAlerts.length}</p>
          <p className="mt-2 text-xs text-slate-400">Detay listesi için tıklayın</p>
        </button>
        <button type="button" onClick={() => setVehicleAlertModalType('inspection')} className="rounded-2xl border border-amber-200 bg-white p-5 text-left shadow-sm transition hover:shadow-md">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">30 Gün İçinde Muayenesi Bitecekler</p>
              <p className="mt-1 text-sm text-slate-500">Özmal araç muayene bitiş uyarıları</p>
            </div>
            <Calendar className="h-5 w-5 text-amber-500" />
          </div>
          <p className="mt-4 text-3xl font-semibold text-amber-600">{inspectionAlerts.length}</p>
          <p className="mt-2 text-xs text-slate-400">Detay listesi için tıklayın</p>
        </button>
        <button type="button" onClick={() => setVehicleAlertModalType('maintenance')} className="rounded-2xl border border-sky-200 bg-white p-5 text-left shadow-sm transition hover:shadow-md">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">Bakımı Yaklaşanlar</p>
              <p className="mt-1 text-sm text-slate-500">30 gün içinde planlı bakıma girecek araçlar</p>
            </div>
            <Settings className="h-5 w-5 text-sky-500" />
          </div>
          <p className="mt-4 text-3xl font-semibold text-sky-600">{maintenanceAlerts.length}</p>
          <p className="mt-2 text-xs text-slate-400">Detay listesi için tıklayın</p>
        </button>
        <button type="button" onClick={() => setOverdueSpotInvoiceModalOpen(true)} className="rounded-2xl border-2 border-rose-300 bg-rose-50 p-5 text-left shadow-sm transition hover:shadow-md hover:border-rose-400">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-rose-900">Spot Faturası Gecikenler</p>
              <p className="mt-1 text-sm text-rose-700">Talep tarihi uzerinden 5 gunu gecen ve faturasi yuklenmemis spot sevkiyatlar</p>
            </div>
            <FileSpreadsheet className="h-5 w-5 text-rose-600" />
          </div>
          <div className="mt-4 text-3xl font-semibold text-rose-600">{showDashboardLoading ? <DashboardPulseValue className="h-10 w-20" /> : (overview?.metrics.overdueSpotInvoices || 0)}</div>
          <p className="mt-2 text-xs text-rose-700">Detay listesi için tıklayın</p>
        </button>
        <button type="button" onClick={() => setOverdueOwnFleetDeliveryModalOpen(true)} className="rounded-2xl border-2 border-orange-300 bg-orange-50 p-5 text-left shadow-sm transition hover:shadow-md hover:border-orange-400">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-orange-900">Özmal Teslim Gecikenler</p>
              <p className="mt-1 text-sm text-orange-700">Talep tarihi uzerinden 5 gun veya daha fazla gecmesine ragmen teslimati kapanmamis ozmal sevkiyatlar</p>
            </div>
            <Truck className="h-5 w-5 text-orange-600" />
          </div>
          <div className="mt-4 text-3xl font-semibold text-orange-600">{showDashboardLoading ? <DashboardPulseValue className="h-10 w-20" /> : (overview?.metrics.overdueOwnFleetDeliveries || 0)}</div>
          <p className="mt-2 text-xs text-orange-700">Detay listesi için tıklayın</p>
        </button>
      </section>

      <section className="space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">Özmal Araçların En Yoğun Olduğu 10 Şehir</p>
              <p className="mt-1 text-sm text-slate-500">Seyir konum adreslerinden türetilen şehir dağılımı</p>
            </div>
            <MapPinned className="h-5 w-5 text-sky-500" />
          </div>
          <div className="mt-5 space-y-3">
            {(overview?.topOwnFleetVehicleCities || []).length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center text-slate-500">Şehir dağılımı için yeterli canlı konum verisi bulunmuyor.</div>
            ) : (
              (overview?.topOwnFleetVehicleCities || []).map((item) => (
                <div key={item.city} className="space-y-1.5">
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="font-medium text-slate-900">{item.city}</span>
                    <span className="text-slate-500">{item.count} araç</span>
                  </div>
                  <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-sky-500" style={{ width: `${Math.max(8, (item.count / topCityMaxCount) * 100)}%` }} />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.1fr_1fr]">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="grid grid-cols-2 gap-2 rounded-xl bg-slate-100 p-1 text-sm">
            <button type="button" onClick={() => setFinanceTab('finance')} className={`rounded-lg px-4 py-2 font-medium ${financeTab === 'finance' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>Finansal Özet</button>
            <button type="button" onClick={() => setFinanceTab('orders')} className={`rounded-lg px-4 py-2 font-medium ${financeTab === 'orders' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>Siparişlerim</button>
          </div>

          <div className="mt-5 flex items-center justify-between">
            <div className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600">
              <Calendar className="h-4 w-4" />
              Bu Ay
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-400">
                <span className="rounded-full bg-slate-100 px-3 py-1">Aylık Finans Karşılaştırması</span>
                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-emerald-600">Canlı</span>
              </div>
            </div>

          {financeTab === 'finance' ? (
            <div className="mt-6 grid gap-4 xl:grid-cols-2">
              {[
                { key: 'currentMonth', title: 'Finansal Özet Bu Ay' },
                { key: 'previousMonth', title: 'Finansal Özet Geçen Ay' },
              ].map((section) => {
                const finance = overview?.finance?.[section.key as 'currentMonth' | 'previousMonth']
                return (
                  <div key={section.key} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                    <h3 className="text-base font-semibold text-slate-900">{section.title}</h3>
                    <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                      <div className="rounded-xl bg-white p-4"><div className="text-slate-500">Toplam Taşıma</div><div className="mt-1 text-2xl font-semibold text-slate-900">{showDashboardLoading ? <DashboardPulseValue className="h-8 w-16" /> : (finance?.shipmentCount || 0)}</div></div>
                      <div className="rounded-xl bg-white p-4"><div className="text-slate-500">Özmal / Spot</div><div className="mt-1 text-2xl font-semibold text-slate-900">{showDashboardLoading ? <DashboardPulseValue className="h-8 w-24" /> : `${finance?.ownFleetCount || 0} / ${finance?.spotCount || 0}`}</div></div>
                      <div className="rounded-xl bg-white p-4"><div className="text-slate-500">Bekleme Bedeli</div><div className="mt-1 text-xl font-semibold text-slate-900">{showDashboardLoading ? <DashboardPulseValue className="h-7 w-24" /> : toCurrency(finance?.waitingTotal || 0)}</div></div>
                      <div className="rounded-xl bg-white p-4"><div className="text-slate-500">Hammaliye Bedeli</div><div className="mt-1 text-xl font-semibold text-slate-900">{showDashboardLoading ? <DashboardPulseValue className="h-7 w-24" /> : toCurrency(finance?.handlingTotal || 0)}</div></div>
                      <div className="rounded-xl bg-white p-4"><div className="text-slate-500">Durak Bedeli</div><div className="mt-1 text-xl font-semibold text-slate-900">{showDashboardLoading ? <DashboardPulseValue className="h-7 w-24" /> : toCurrency(finance?.stopTotal || 0)}</div></div>
                      <div className="rounded-xl bg-white p-4"><div className="text-slate-500">Toplam KM</div><div className="mt-1 text-xl font-semibold text-slate-900">{showDashboardLoading ? <DashboardPulseValue className="h-7 w-24" /> : `${Number(finance?.totalKm || 0).toLocaleString('tr-TR')} km`}</div></div>
                      <div className="col-span-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4"><div className="text-emerald-700">Spota Toplam Ödeme</div><div className="mt-1 text-2xl font-semibold text-emerald-700">{showDashboardLoading ? <DashboardPulseValue className="h-8 w-32" /> : toCurrency(finance?.totalSpotPayment || 0)}</div></div>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <p className="text-sm text-slate-500">Plaka Atanmayan</p>
                <div className="mt-2 text-3xl font-semibold text-slate-900">{showDashboardLoading ? <DashboardPulseValue className="h-10 w-20" /> : (overview?.metrics.unassignedOrders || 0)}</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <p className="text-sm text-slate-500">Açık Teklif</p>
                <div className="mt-2 text-3xl font-semibold text-slate-900">{showDashboardLoading ? <DashboardPulseValue className="h-10 w-20" /> : (overview?.metrics.openBidRequests || 0)}</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <p className="text-sm text-slate-500">Teslim Onayı Bekleyen</p>
                <div className="mt-2 text-3xl font-semibold text-slate-900">{showDashboardLoading ? <DashboardPulseValue className="h-10 w-20" /> : (overview?.metrics.deliveryApprovalPending || 0)}</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <p className="text-sm text-slate-500">Ödeme Bekleyen</p>
                <div className="mt-2 text-3xl font-semibold text-slate-900">{showDashboardLoading ? <DashboardPulseValue className="h-10 w-20" /> : (overview?.metrics.paymentPending || 0)}</div>
              </div>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Hızlı İşlemler</h2>
              <p className="mt-1 text-sm text-slate-500">Sık kullanılan işlemlerinize hızlı erişim</p>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-400"><span className="h-2 w-2 rounded-full bg-emerald-500" />Otomatik güncelleniyor</div>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {quickActions.map((action) => {
              const Icon = action.icon
              return (
                <Link key={action.title} to={action.to} className="group flex items-center justify-between rounded-2xl border border-slate-200 p-4 transition hover:border-slate-300 hover:shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${action.accent}`}><Icon className="h-5 w-5" /></div>
                    <div>
                      <p className="font-semibold text-slate-900">{action.title}</p>
                      <p className="text-sm text-slate-500">{action.subtitle}</p>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-300 transition group-hover:text-slate-500" />
                </Link>
              )
            })}

            <Link to="/support-center" className="group flex items-center justify-between rounded-2xl border border-slate-200 p-4 transition hover:border-slate-300 hover:shadow-sm">
              <div className="flex items-center gap-3">
                <div className="relative flex h-11 w-11 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                  <LifeBuoy className="h-5 w-5" />
                  {supportUnreadCount > 0 && (
                    <span className="absolute -right-1 -top-1 inline-flex min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">{supportUnreadCount}</span>
                  )}
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Destek</p>
                  <p className="text-sm text-slate-500">{supportUnreadCount > 0 ? `${supportUnreadCount} yeni mesaj var` : 'Destek mesajlarını görüntüle'}</p>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-300 transition group-hover:text-slate-500" />
            </Link>
            <Link to="/driver-incident-reports" className="group flex items-center justify-between rounded-2xl border border-slate-200 p-4 transition hover:border-slate-300 hover:shadow-sm">
              <div className="flex items-center gap-3">
                <div className="relative flex h-11 w-11 items-center justify-center rounded-xl bg-rose-100 text-rose-700">
                  <AlertTriangle className="h-5 w-5" />
                  {openIncidentCount > 0 && (
                    <span className="absolute -right-1 -top-1 inline-flex min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">{openIncidentCount}</span>
                  )}
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Kaza / Arıza</p>
                  <p className="text-sm text-slate-500">{openIncidentCount > 0 ? `${openIncidentCount} açık kayıt var` : 'Açık kayıt bulunmuyor'}</p>
                  {openIncidentBreakdown.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {openIncidentBreakdown.map((item) => (
                        <span key={item.plate} className="inline-flex items-center rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-700">
                          {item.plate}: {item.count}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-300 transition group-hover:text-slate-500" />
            </Link>
            <Link to="/reports" className="group flex items-center justify-between rounded-2xl border border-slate-200 p-4 transition hover:border-slate-300 hover:shadow-sm">
              <div>
                <p className="font-semibold text-slate-900">Raporlar</p>
                <p className="text-sm text-slate-500">Rapor ve analitik ekranına git</p>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-300 transition group-hover:text-slate-500" />
            </Link>
          </div>
        </div>
      </section>

      <SupplyFormModal
        isOpen={supplyModalOpen}
        onClose={() => setSupplyModalOpen(false)}
        onSave={submitSupplyModal}
        initialData={{ durum: 'teklif-topla', aracTipi: 'Spot' }}
      />

      <ShipmentWizardModal
        isOpen={wizardOpen}
        onClose={closeWizard}
        onCreated={refreshOverview}
      />

      {activeVehicleAlert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
          <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-5">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">{activeVehicleAlert.title}</h2>
                <p className="mt-1 text-sm text-slate-500">Araç ve son tarih bilgileri tam tarih formatında listelenir.</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={exportVehicleAlerts} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                  <FileSpreadsheet className="h-4 w-4" />
                  Excele Aktar
                </button>
                <button type="button" onClick={() => setVehicleAlertModalType(null)} className="rounded-xl border border-slate-200 p-2 text-slate-500 hover:bg-slate-50">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="overflow-auto p-6">
              {activeVehicleAlert.items.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center text-slate-500">Önümüzdeki 30 gün için kayıt bulunmuyor.</div>
              ) : (
                <div className="overflow-x-auto rounded-2xl border border-slate-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Çekici</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Dorse</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Bitiş / Plan Tarihi</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Kalan Gün</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {activeVehicleAlert.items.map((item) => (
                        <tr key={`${activeVehicleAlert.title}-${item.id}`}>
                          <td className="px-4 py-3 font-medium text-slate-900">{item.plate}</td>
                          <td className="px-4 py-3 text-slate-700">{item.trailerPlate || '-'}</td>
                          <td className="px-4 py-3 text-slate-700">{formatDateTimeTr(item.date)}</td>
                          <td className={`px-4 py-3 font-semibold ${activeVehicleAlert.accent}`}>{item.daysLeft} gün</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {unusedDigitalLicenseModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
          <div className="flex max-h-[90vh] w-full max-w-7xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-5">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">72 Saatten Fazla Kullanılmayan Dijital Ehliyetler</h2>
                <p className="mt-1 text-sm text-slate-500">Özmal sürücülerinin Seyir'de herhangi bir araçta kartlarının son görünme zamanı ve son 30 günlük kullanım özeti.</p>
              </div>
              <button type="button" onClick={() => setUnusedDigitalLicenseModalOpen(false)} className="rounded-xl border border-slate-200 p-2 text-slate-500 hover:bg-slate-50">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-auto p-6">
              {unusedDigitalLicenseLoading ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center text-slate-500">Seyir kullanım geçmişi yükleniyor...</div>
              ) : unusedDigitalLicenseDrivers.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center text-slate-500">72 saatten uzun süredir kullanılmayan dijital ehliyet bulunmuyor.</div>
              ) : (
                <div className="space-y-3">
                  {unusedDigitalLicenseDrivers.map((driver) => (
                    <details key={driver.driverId} className="rounded-2xl border border-slate-200 bg-white">
                      <summary className="cursor-pointer list-none px-4 py-4">
                        <div className="grid gap-3 md:grid-cols-[minmax(180px,1.3fr)_minmax(130px,1fr)_minmax(150px,1fr)_minmax(120px,0.8fr)] md:items-center">
                          <div>
                            <div className="font-semibold text-slate-900">{driver.driverName}</div>
                            <div className="mt-1 text-xs text-slate-500">Ehliyet: {driver.licenseNumber}</div>
                          </div>
                          <div className="text-sm text-slate-700">Atanan araç: <span className="font-semibold">{driver.assignedPlate}</span></div>
                          <div className="text-sm text-slate-700">Son kart aracı: <span className="font-semibold">{driver.lastCardVehiclePlate}</span><div className="text-xs text-slate-500">{driver.lastCardSeenAt ? formatDateTimeTr(driver.lastCardSeenAt) : 'Hiç görünmedi'}</div></div>
                          <div className="font-semibold text-violet-600">{driver.unusedHours == null ? '72+ saat' : `${driver.unusedHours} saat`}</div>
                        </div>
                      </summary>
                      <div className="border-t border-slate-200 bg-slate-50 p-4">
                        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Son 30 Günlük Araç Kullanımı</div>
                        {driver.dailyUsage.length === 0 ? (
                          <div className="text-sm text-slate-500">Kayıt bulunamadı.</div>
                        ) : (
                          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                            <table className="min-w-full text-sm">
                              <thead className="bg-slate-100">
                                <tr>
                                  <th className="px-3 py-2 text-left font-medium text-slate-600">Gün</th>
                                  <th className="px-3 py-2 text-left font-medium text-slate-600">Araç</th>
                                  <th className="px-3 py-2 text-left font-medium text-slate-600">İlk Görünme</th>
                                  <th className="px-3 py-2 text-left font-medium text-slate-600">Son Görünme</th>
                                  <th className="px-3 py-2 text-right font-medium text-slate-600">Günlük KM</th>
                                  <th className="px-3 py-2 text-left font-medium text-slate-600">Sürüş</th>
                                  <th className="px-3 py-2 text-left font-medium text-slate-600">Çalışma</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-200">
                                {driver.dailyUsage.map((usage) => (
                                  <tr key={`${driver.driverId}-${usage.usage_date}`}>
                                    <td className="px-3 py-2 text-slate-700">{usage.usage_date}</td>
                                    <td className="px-3 py-2 font-medium text-slate-900">{usage.vehicle_plate || '-'}</td>
                                    <td className="px-3 py-2 text-slate-700">{usage.first_seen_at ? formatDateTimeTr(usage.first_seen_at) : '-'}</td>
                                    <td className="px-3 py-2 text-slate-700">{usage.last_seen_at ? formatDateTimeTr(usage.last_seen_at) : '-'}</td>
                                    <td className="px-3 py-2 text-right text-slate-700">{usage.daily_km == null ? '-' : Number(usage.daily_km).toLocaleString('tr-TR')}</td>
                                    <td className="px-3 py-2 text-slate-700">{usage.daily_drive_time || '-'}</td>
                                    <td className="px-3 py-2 text-slate-700">{usage.daily_work_time || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </details>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {seyirViolationModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
          <div className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-5">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Seyir Ehliyet İhlalleri</h2>
                <p className="mt-1 text-sm text-slate-500">Seyir'den tespit edilen dijital ehliyet uyumsuzluk geçmişi listelenir.</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={exportSeyirViolations} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                  <FileSpreadsheet className="h-4 w-4" />
                  Excele Aktar
                </button>
                <button type="button" onClick={() => setSeyirViolationModalOpen(false)} className="rounded-xl border border-slate-200 p-2 text-slate-500 hover:bg-slate-50">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="overflow-auto p-6">
              {seyirViolations.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center text-slate-500">Seyir ehliyet ihlali kaydı bulunmuyor.</div>
              ) : (
                <div className="overflow-x-auto rounded-2xl border border-slate-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Son Tespit</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">İhlal Tipi</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Çekici / Dorse</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Araç Sürücüsü</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Araç Ehliyet No</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Seyir Ehliyet No</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Seyir Sürücü</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Tekrar</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {seyirViolations.map((row) => (
                        <tr key={row.id}>
                          <td className="px-4 py-3 text-slate-700">{formatDateTimeTr(row.last_detected_at)}</td>
                          <td className="px-4 py-3 font-semibold text-rose-600">{getSeyirViolationLabel(row.violation_type)}</td>
                          <td className="px-4 py-3 text-slate-700">{row.tractor_plate || '-'} / {row.trailer_plate || '-'}</td>
                          <td className="px-4 py-3 text-slate-700">{row.system_driver_name || '-'}</td>
                          <td className="px-4 py-3 text-slate-700">{row.system_driver_license_no || '-'}</td>
                          <td className="px-4 py-3 font-semibold text-slate-900">{row.seyir_digital_license_no || '-'}</td>
                          <td className="px-4 py-3 text-slate-700">{row.seyir_driver_name || '-'}</td>
                          <td className="px-4 py-3 text-slate-700">{row.check_count || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {overdueSpotInvoiceModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
          <div className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-5">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Spot Faturası Geciken Sevkiyatlar</h2>
                <p className="mt-1 text-sm text-slate-500">Talep tarihi uzerinden 5 gun veya daha fazla gecmis, faturasi yuklenmemis spot sevkiyatlar.</p>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => exportDelayedShipments(overview?.overdueSpotInvoices || [], 'spot-faturasi-geciken-sevkiyatlar')} className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100">
                  <FileSpreadsheet className="h-4 w-4" /> Excele Aktar
                </button>
                <button type="button" onClick={() => setOverdueSpotInvoiceModalOpen(false)} className="rounded-xl border border-slate-200 p-2 text-slate-500 hover:bg-slate-50">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="overflow-auto p-6">
              {(overview?.overdueSpotInvoices || []).length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center text-slate-500">Geciken spot fatura kaydi bulunmuyor.</div>
              ) : (
                <div className="overflow-x-auto rounded-2xl border border-slate-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Sevk No</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Plan</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Yukleme</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Teslim</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Talep Tarihi</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Gecikme</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Surucu</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Cekici / Dorse</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {(overview?.overdueSpotInvoices || []).map((item) => (
                        <tr key={item.id}>
                          <td className="px-4 py-3 font-medium text-slate-900">{item.shipmentNo}</td>
                          <td className="px-4 py-3 text-slate-700">{item.planCode || '-'}</td>
                          <td className="px-4 py-3 text-slate-700">{item.pickupName} / {item.pickupCity}</td>
                          <td className="px-4 py-3 text-slate-700">{item.deliveryName} / {item.deliveryCity}</td>
                          <td className="px-4 py-3 text-slate-700">{formatDateOnlyTr(item.pickupDate)}</td>
                          <td className="px-4 py-3 font-semibold text-rose-600">{item.overdueDays} gün</td>
                          <td className="px-4 py-3 text-slate-700">{item.driverName}{item.driverPhone && item.driverPhone !== '-' ? ` / ${item.driverPhone}` : ''}</td>
                          <td className="px-4 py-3 text-slate-700">{item.tractorPlate || '-'} / {item.trailerPlate || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {overdueOwnFleetDeliveryModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
          <div className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-5">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Özmal Teslim Geciken Sevkiyatlar</h2>
                <p className="mt-1 text-sm text-slate-500">Talep tarihi uzerinden 5 gun veya daha fazla gecmis, teslimati kapanmamis ozmal sevkiyatlar.</p>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => exportDelayedShipments(overview?.overdueOwnFleetDeliveries || [], 'ozmal-teslim-geciken-sevkiyatlar')} className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100">
                  <FileSpreadsheet className="h-4 w-4" /> Excele Aktar
                </button>
                <button type="button" onClick={() => setOverdueOwnFleetDeliveryModalOpen(false)} className="rounded-xl border border-slate-200 p-2 text-slate-500 hover:bg-slate-50">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="overflow-auto p-6">
              {(overview?.overdueOwnFleetDeliveries || []).length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center text-slate-500">Geciken özmal teslim kaydi bulunmuyor.</div>
              ) : (
                <div className="overflow-x-auto rounded-2xl border border-slate-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Sevk No</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Plan</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Yukleme</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Teslim</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Talep Tarihi</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Gecikme</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Surucu</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Cekici / Dorse</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {(overview?.overdueOwnFleetDeliveries || []).map((item) => (
                        <tr key={item.id}>
                          <td className="px-4 py-3 font-medium text-slate-900">{item.shipmentNo}</td>
                          <td className="px-4 py-3 text-slate-700">{item.planCode || '-'}</td>
                          <td className="px-4 py-3 text-slate-700">{item.pickupName} / {item.pickupCity}</td>
                          <td className="px-4 py-3 text-slate-700">{item.deliveryName} / {item.deliveryCity}</td>
                          <td className="px-4 py-3 text-slate-700">{formatDateOnlyTr(item.pickupDate)}</td>
                          <td className="px-4 py-3 font-semibold text-orange-600">{item.overdueDays} gün</td>
                          <td className="px-4 py-3 text-slate-700">{item.driverName}{item.driverPhone && item.driverPhone !== '-' ? ` / ${item.driverPhone}` : ''}</td>
                          <td className="px-4 py-3 text-slate-700">{item.tractorPlate || '-'} / {item.trailerPlate || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {false && wizardOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-6 backdrop-blur-sm">
          <div className="max-h-[92vh] w-full max-w-5xl overflow-auto rounded-3xl bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-6 py-5">
              <div>
                <h2 className="text-2xl font-semibold text-slate-900">Sevk Siparişi Aç</h2>
                <p className="mt-1 text-sm text-slate-500">3 adımda yeni bir tedarik talebi oluşturun</p>
              </div>
              <button type="button" onClick={closeWizard} className="rounded-xl border border-slate-200 p-2 text-slate-500 hover:bg-slate-50"><X className="h-5 w-5" /></button>
            </div>

            <div className="px-6 py-5">
              <ShipmentWizardStepTabs currentStep={wizardStep} onStepClick={handleStepClick} />

              <div className="w-full">
                <div className={`space-y-4 ${wizardStep === 1 ? '' : 'hidden'}`}>
                  <ShipmentWizardSection title="Sefer Tipi">
                    <div className="grid grid-cols-2 gap-3">
                      <button type="button" onClick={() => setWizardForm((c) => ({ ...c, tripType: 'komple' }))} className={`rounded-xl border px-4 py-3 text-sm font-medium ${wizardForm.tripType === 'komple' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600'}`}>Komple</button>
                      <button type="button" onClick={() => setWizardForm((c) => ({ ...c, tripType: 'parsiyel' }))} className={`rounded-xl border px-4 py-3 text-sm font-medium ${wizardForm.tripType === 'parsiyel' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600'}`}>Parsiyel</button>
                    </div>
                  </ShipmentWizardSection>

                  <ShipmentWizardSection title="Rota Bilgileri">
                    <div className="space-y-3">
                      <div>
                        <div className="mb-2 flex items-center justify-between gap-3">
                          <label className="block text-xs font-medium uppercase tracking-wide text-slate-500">Başlangıç</label>
                          <button type="button" onClick={() => openCustomerModal('pickup')} className="text-xs font-medium text-emerald-700 hover:text-emerald-800">+ Yeni Ekle</button>
                        </div>
                        <input value={pickupSearch} onChange={(e) => setPickupSearch(e.target.value)} placeholder="Yükleme noktası yazarak arayın" className="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-700 outline-none focus:border-emerald-400" />
                        <div className="mt-2 max-h-40 overflow-auto rounded-xl border border-slate-200 bg-white">
                          {filteredPickupOptions.slice(0, 8).map((option) => <button key={option.id} type="button" onClick={() => updateRouteSelection('pickup', option.id)} className={`flex w-full items-start justify-between gap-3 px-4 py-3 text-left hover:bg-white/5 ${wizardForm.pickupOptionId === option.id ? 'bg-emerald-950/30' : ''}`}><div><div className="text-sm font-medium text-slate-800">{option.label}</div><div className="text-xs text-slate-500">{[option.city, option.district, option.address].filter(Boolean).join(' / ') || '-'}</div></div><span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] uppercase text-slate-500">{option.type}</span></button>)}
                          {filteredPickupOptions.length === 0 && <div className="px-4 py-3 text-sm text-slate-400">Sonuç bulunamadı</div>}
                        </div>
                      </div>
                      <div>
                        <button type="button" onClick={() => setShowStopPicker((current) => !current)} className="w-full rounded-xl border border-dashed border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 hover:bg-emerald-100">+ Ara Nokta Ekle ({intermediateStops.length}/30)</button>
                        {intermediateStops.length > 0 && (
                          <div className="mt-2 space-y-2 rounded-xl border border-slate-200 bg-white p-3">
                            {intermediateStops.map((stop, index) => (
                              <div
                                key={stop.id}
                                draggable
                                onDragStart={() => setDraggingStopId(stop.id)}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => {
                                  if (draggingStopId) moveIntermediateStop(draggingStopId, stop.id)
                                  setDraggingStopId(null)
                                }}
                                onDragEnd={() => setDraggingStopId(null)}
                                className={`flex items-start justify-between gap-3 rounded-lg px-3 py-2 cursor-move ${draggingStopId === stop.id ? 'bg-emerald-100' : 'bg-slate-50'}`}
                              >
                                <div>
                                  <div className="text-sm font-medium text-slate-800">{index + 1}. {stop.label}</div>
                                  <div className="text-xs text-slate-500">{[stop.city, stop.district, stop.address].filter(Boolean).join(' / ') || '-'}</div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-[10px] uppercase tracking-wide text-slate-400">Sürükle</span>
                                  <button type="button" onClick={() => removeIntermediateStop(stop.id)} className="text-xs font-medium text-rose-600 hover:text-rose-700">Sil</button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {showStopPicker && (
                          <div className="mt-2 rounded-xl border border-slate-200 bg-white p-3">
                            <input value={stopSearch} onChange={(e) => setStopSearch(e.target.value)} placeholder="Ara nokta yazarak arayın" className="h-11 w-full rounded-xl border border-slate-200 px-4 text-sm outline-none focus:border-emerald-400" />
                            <div className="mt-2 max-h-40 overflow-auto rounded-xl border border-slate-200">
                              {filteredStopOptions.slice(0, 8).map((option) => (
                                <button key={option.id} type="button" onClick={() => addIntermediateStop(option.id)} className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50">
                                  <div>
                                    <div className="text-sm font-medium text-slate-800">{option.label}</div>
                                    <div className="text-xs text-slate-500">{[option.city, option.district, option.address].filter(Boolean).join(' / ') || '-'}</div>
                                  </div>
                                  <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] uppercase text-slate-500">{option.type}</span>
                                </button>
                              ))}
                              {filteredStopOptions.length === 0 && <div className="px-4 py-3 text-sm text-slate-400">Uygun ara nokta bulunamadı</div>}
                            </div>
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="mb-2 flex items-center justify-between gap-3">
                          <label className="block text-xs font-medium uppercase tracking-wide text-slate-500">Bitiş</label>
                          <button type="button" onClick={() => openCustomerModal('delivery')} className="text-xs font-medium text-emerald-700 hover:text-emerald-800">+ Yeni Ekle</button>
                        </div>
                        <input value={deliverySearch} onChange={(e) => setDeliverySearch(e.target.value)} placeholder="Boşaltma noktası yazarak arayın" className="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-700 outline-none focus:border-emerald-400" />
                        <div className="mt-2 max-h-40 overflow-auto rounded-xl border border-slate-200 bg-white">
                          {filteredDeliveryOptions.slice(0, 8).map((option) => <button key={option.id} type="button" onClick={() => updateRouteSelection('delivery', option.id)} className={`flex w-full items-start justify-between gap-3 px-4 py-3 text-left hover:bg-white/5 ${wizardForm.deliveryOptionId === option.id ? 'bg-emerald-950/30' : ''}`}><div><div className="text-sm font-medium text-slate-800">{option.label}</div><div className="text-xs text-slate-500">{[option.city, option.district, option.address].filter(Boolean).join(' / ') || '-'}</div></div><span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] uppercase text-slate-500">{option.type}</span></button>)}
                          {filteredDeliveryOptions.length === 0 && <div className="px-4 py-3 text-sm text-slate-400">Sonuç bulunamadı</div>}
                        </div>
                      </div>
                    </div>
                  </ShipmentWizardSection>

                  <ShipmentWizardSection title="Araç Tipi (birden çok seçilebilir)">
                    <MultiVehicleTypeSelect value={wizardForm.vehicleType} onChange={(v) => setWizardForm((c) => ({ ...c, vehicleType: v }))} />
                  </ShipmentWizardSection>
                </div>

                <div className={`space-y-4 ${wizardStep === 2 ? '' : 'hidden'}`}>
                  <ShipmentWizardSection title="Yük Miktarı ve Detaylar">
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                      <>
                        <input list="load-type-options" value={wizardForm.loadType} onChange={(e) => setWizardForm((c) => ({ ...c, loadType: e.target.value }))} placeholder="Yük türü (örn. cips)" className="h-12 rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-emerald-400 md:col-span-3" />
                        <datalist id="load-type-options">
                          {LOAD_TYPE_OPTIONS.map((item) => <option key={item} value={item} />)}
                        </datalist>
                      </>
                      <input type="number" value={wizardForm.weightKg || ''} onChange={(e) => setWizardForm((c) => ({ ...c, weightKg: Number(e.target.value || 0) }))} placeholder="Ağırlık kg" className="h-12 rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-emerald-400" />
                      <input type="number" value={wizardForm.volumeM3 || ''} onChange={(e) => setWizardForm((c) => ({ ...c, volumeM3: Number(e.target.value || 0) }))} placeholder="Hacim m3" className="h-12 rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-emerald-400" />
                      <input type="number" value={wizardForm.palletCount || ''} onChange={(e) => setWizardForm((c) => ({ ...c, palletCount: Number(e.target.value || 0) }))} placeholder="Palet" className="h-12 rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-emerald-400" />
                      <input type="number" value={wizardForm.vehicleCount || ''} onChange={(e) => setWizardForm((c) => ({ ...c, vehicleCount: Number(e.target.value || 1) }))} placeholder="Araç adedi" className="h-12 rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-emerald-400" />
                      <div className="col-span-2 flex gap-2 md:col-span-1">
                        <input type="number" value={wizardForm.distanceKm || ''} onChange={(e) => { setDistanceEdited(true); setWizardForm((c) => ({ ...c, distanceKm: Number(e.target.value || 0) })) }} placeholder="Mesafe km" className="h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-emerald-400" />
                        <button type="button" onClick={() => { setDistanceEdited(false); setWizardForm((c) => ({ ...c, distanceKm: estimatedDistanceKm })) }} className="rounded-xl border border-slate-200 px-3 text-sm font-medium text-slate-600 hover:bg-slate-50">Tahmini</button>
                      </div>
                      <div className="col-span-2 rounded-xl border border-blue-800/30 bg-blue-950/30 px-4 py-3 text-sm text-slate-200 md:col-span-2">
                        <div className="grid grid-cols-2 gap-3">
                          <div>Toplam Ağırlık: <span className="font-semibold text-slate-900">{wizardForm.weightKg || 0} kg</span></div>
                          <div>Toplam Hacim: <span className="font-semibold text-slate-900">{wizardForm.volumeM3 || 0} m3</span></div>
                          <div>Mesafe: <span className="font-semibold text-slate-900">{effectiveDistanceKm || 0} km</span></div>
                          <div>Yük Satırı: <span className="font-semibold text-slate-900">1</span></div>
                        </div>
                      </div>
                    </div>
                    <textarea value={wizardForm.notes} onChange={(e) => setWizardForm((c) => ({ ...c, notes: e.target.value }))} placeholder="Açıklama / not" className="mt-3 min-h-24 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-emerald-400" />
                  </ShipmentWizardSection>
                </div>

                <div className={`space-y-4 ${wizardStep === 3 ? '' : 'hidden'}`}>
                  <div className="rounded-2xl bg-slate-100 p-0 overflow-hidden">
                    <div className="flex items-center justify-between bg-slate-400 px-4 py-3 text-white">
                      <div className="text-sm font-semibold">Özet & Maliyet</div>
                      <div className="rounded-lg bg-white/20 px-2 py-1 text-xs">TL</div>
                    </div>
                    <div className="space-y-4 p-4">
                      <div className="space-y-2 text-sm text-slate-600">
                        <div className="flex items-center justify-between"><span>Navlun (Baz)</span><span>{toCurrency(regionalBasePrice > 0 ? regionalBasePrice : estimatedBaseCost)}</span></div>
                        <div className="flex items-center justify-between"><span>Mesafe</span><span>{effectiveDistanceKm.toLocaleString('tr-TR')} km</span></div>
                        <div className="flex items-center justify-between"><span>Araç Tipi</span><span>{wizardForm.vehicleType?.length ? wizardForm.vehicleType.join(', ') : '-'}</span></div>
                        <div className="flex items-center justify-between"><span>Yük Türü</span><span>{wizardForm.loadType || '-'}</span></div>
                      </div>

                      <div className="border-t border-slate-200 pt-4">
                        <div className="mb-3 flex items-center justify-between text-sm font-medium text-slate-700">
                          <span>Kâr Marjı</span>
                          <span>% {wizardForm.marginPercent}</span>
                        </div>
                        <input type="range" min="0" max="40" step="1" value={wizardForm.marginPercent} onChange={(e) => setWizardForm((c) => ({ ...c, marginPercent: Number(e.target.value) }))} className="w-full" />
                      </div>

                      <div className="rounded-2xl bg-emerald-100 px-4 py-5">
                        <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Toplam Teklif</div>
                        <div className="mt-2 text-4xl font-bold text-emerald-700">{toCurrency(estimatedOffer)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between border-t border-slate-200 pt-5">
                <button type="button" onClick={() => setWizardStep((current) => Math.max(1, current - 1) as WizardStep)} disabled={wizardStep === 1} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-sm font-medium text-slate-600 disabled:opacity-40"><ChevronLeft className="h-4 w-4" />Geri</button>
                <div className="flex items-center gap-3">
                  {wizardStep < 3 ? (
                    <button type="button" onClick={handleNextStep} className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white">İleri<ChevronRight className="h-4 w-4" /></button>
                  ) : (
                    <button type="button" onClick={submitWizard} disabled={wizardSubmitting} className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-3 text-sm font-semibold text-white disabled:opacity-60">
                      <Plus className="h-4 w-4" />
                      {wizardSubmitting ? 'Oluşturuluyor...' : 'Sevk Siparişi Aç'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {customerModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm">
          <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between bg-slate-950 px-6 py-5 text-white">
              <div>
                <h3 className="text-xl font-semibold">Yeni Müşteri Ekle</h3>
                <p className="mt-1 text-sm text-slate-300">Müşteri bilgilerini giriniz</p>
              </div>
              <button type="button" onClick={() => setCustomerModalOpen(false)} className="rounded-xl border border-white/10 p-2 text-slate-300 hover:bg-white/5"><X className="h-5 w-5" /></button>
            </div>

            <div className="space-y-5 overflow-y-auto px-6 py-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Cari Kodu</label>
                  <input value={customerForm.companyCode} onChange={(e) => setCustomerForm((c) => ({ ...c, companyCode: e.target.value.toLocaleUpperCase('tr-TR') }))} placeholder="CST-0005" className="h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm outline-none focus:border-blue-400" />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Fatura Başlığı</label>
                  <input value={customerForm.companyName} onChange={(e) => setCustomerForm((c) => ({ ...c, companyName: e.target.value.toLocaleUpperCase('tr-TR') }))} placeholder="Örn: DOĞUŞ ÇAY SANAYİ A.Ş." className="h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm outline-none focus:border-blue-400" />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">VKN / TC</label>
                  <input value={customerForm.taxNumber} onChange={(e) => setCustomerForm((c) => ({ ...c, taxNumber: e.target.value }))} placeholder="1234567890" className="h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm outline-none focus:border-blue-400" />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Vergi Dairesi</label>
                  <input value={customerForm.taxOffice} onChange={(e) => setCustomerForm((c) => ({ ...c, taxOffice: e.target.value.toLocaleUpperCase('tr-TR') }))} placeholder="Ümraniye" className="h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm outline-none focus:border-blue-400" />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">İletişim Kişisi</label>
                  <input value={customerForm.contactPerson} onChange={(e) => setCustomerForm((c) => ({ ...c, contactPerson: e.target.value.toLocaleUpperCase('tr-TR') }))} placeholder="Ad Soyad" className="h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm outline-none focus:border-blue-400" />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Telefon</label>
                  <input value={customerForm.phone} onChange={(e) => setCustomerForm((c) => ({ ...c, phone: e.target.value }))} placeholder="05XX XXX XX XX" className="h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm outline-none focus:border-blue-400" />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Fatura E-Posta</label>
                  <input value={customerForm.email} onChange={(e) => setCustomerForm((c) => ({ ...c, email: e.target.value }))} placeholder="fatura@firma.com" className="h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm outline-none focus:border-blue-400" />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Ödeme Tipi</label>
                  <select value={customerForm.paymentType} onChange={(e) => setCustomerForm((c) => ({ ...c, paymentType: e.target.value as CustomerCreateForm['paymentType'] }))} className="h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm outline-none focus:border-blue-400">
                    <option value="7">7 GÜN</option>
                    <option value="14">14 GÜN</option>
                    <option value="30">30 GÜN</option>
                    <option value="45">45 GÜN</option>
                    <option value="60">60 GÜN</option>
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">İl</label>
                  <input value={customerForm.city} onChange={(e) => setCustomerForm((c) => ({ ...c, city: e.target.value.toLocaleUpperCase('tr-TR') }))} placeholder="İl" className="h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm outline-none focus:border-blue-400" />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">İlçe</label>
                  <input value={customerForm.district} onChange={(e) => setCustomerForm((c) => ({ ...c, district: e.target.value.toLocaleUpperCase('tr-TR') }))} placeholder="İlçe" className="h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm outline-none focus:border-blue-400" />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Fatura Adresi</label>
                <input ref={customerAddressInputRef} value={customerForm.address} onChange={(e) => setCustomerForm((c) => ({ ...c, address: e.target.value.toLocaleUpperCase('tr-TR') }))} placeholder="Mahalle, Sokak, No, İlçe/İl" className="h-12 w-full rounded-2xl border border-slate-200 px-4 text-sm outline-none focus:border-blue-400" />
                <div className="mt-2 text-xs text-slate-400">Adres seçildiğinde il, ilçe ve koordinatlar otomatik dolar.</div>
                {(customerForm.latitude != null && customerForm.longitude != null) ? (
                  <div className="mt-2 text-xs text-emerald-600">Konum hazır: {customerForm.latitude.toFixed(6)}, {customerForm.longitude.toFixed(6)}</div>
                ) : null}
              </div>
            </div>

            <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-slate-100 bg-white px-6 py-5">
              <button type="button" onClick={() => setCustomerModalOpen(false)} className="rounded-xl px-5 py-3 text-sm font-medium text-slate-600 hover:bg-slate-50">Vazgeç</button>
              <button type="button" onClick={() => saveCustomerAndSelect()} disabled={customerSubmitting} className="rounded-xl bg-blue-100 px-5 py-3 text-sm font-semibold text-blue-700 disabled:opacity-60">{customerSubmitting ? 'Kaydediliyor...' : 'Kaydet ve Seç'}</button>
            </div>
          </div>
        </div>
      )}

      {newSpotDriversModalOpen && (() => {
        const activeDrivers = newSpotDrivers?.drivers[newSpotDriversTab] || []
        const breakdown = getSourceBreakdown(activeDrivers)
        const todayBreakdown = getSourceBreakdown(newSpotDrivers?.drivers.today || [])
        const weekBreakdown = getSourceBreakdown(newSpotDrivers?.drivers.week || [])
        const monthBreakdown = getSourceBreakdown(newSpotDrivers?.drivers.month || [])
        return (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm" onClick={() => setNewSpotDriversModalOpen(false)}>
            <div className="flex max-h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
              <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Yeni Spot Sürücüler</h3>
                  <p className="mt-1 text-sm text-slate-500">Kayıt kaynağı ve onay bilgileri</p>
                </div>
                <button type="button" onClick={() => setNewSpotDriversModalOpen(false)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
              </div>

              <div className="grid grid-cols-3 gap-4 border-b border-slate-200 px-5 py-4">
                {([
                  { key: 'today' as const, label: 'Bugün', breakdown: todayBreakdown, count: newSpotDrivers?.counts.today || 0 },
                  { key: 'week' as const, label: 'Bu Hafta', breakdown: weekBreakdown, count: newSpotDrivers?.counts.week || 0 },
                  { key: 'month' as const, label: 'Bu Ay', breakdown: monthBreakdown, count: newSpotDrivers?.counts.month || 0 },
                ]).map((period) => (
                  <button key={period.key} type="button" onClick={() => setNewSpotDriversTab(period.key)} className={`rounded-xl border p-4 text-left transition ${newSpotDriversTab === period.key ? 'border-emerald-300 bg-emerald-50 shadow-sm' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{period.label}</div>
                    <div className="mt-1.5 text-2xl font-bold text-slate-900">{period.count}</div>
                    <div className="mt-3">
                      <SourcePieChart breakdown={period.breakdown} total={period.count} />
                    </div>
                  </button>
                ))}
              </div>

              <div className="overflow-y-auto p-5">
                {activeDrivers.length === 0 ? (
                  <p className="py-8 text-center text-sm text-slate-500">Bu dönemde yeni spot sürücü kaydı yok.</p>
                ) : (
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-3">Sürücü</th><th className="px-4 py-3">Konum</th><th className="px-4 py-3">Kayıt Kaynağı</th><th className="px-4 py-3">Durum</th><th className="px-4 py-3">Kayıt Tarihi</th></tr></thead>
                      <tbody>
                        {activeDrivers.map((driver) => (
                          <tr key={driver.id} className="border-t border-slate-100">
                            <td className="px-4 py-3"><div className="font-semibold text-slate-900">{driver.full_name}</div><div className="text-xs text-slate-500">{driver.phone || '-'}</div></td>
                            <td className="px-4 py-3 text-slate-600">{[driver.city, driver.district].filter(Boolean).join(' / ') || '-'}</td>
                            <td className="px-4 py-3"><div className="font-medium text-slate-700">{driver.registration_source}</div>{driver.reviewer_name && <div className="text-xs text-slate-500">Ekleyen/onaylayan: {driver.reviewer_name}</div>}</td>
                            <td className="px-4 py-3"><span className={`rounded-full px-2 py-1 text-xs font-semibold ${driver.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : driver.status === 'rejected' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-800'}`}>{driver.status === 'approved' ? 'Onaylandı' : driver.status === 'rejected' ? 'Reddedildi' : 'Beklemede'}</span></td>
                            <td className="whitespace-nowrap px-4 py-3 text-slate-600">{formatDateTimeTr(driver.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      })()}
    </div>
  )
}
